import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild, ElementRef } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { AuthService } from "../../../core/services/auth.service";
import { CommonModule } from "@angular/common";
import { FullCalendarModule } from "@fullcalendar/angular";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import esLocale from '@fullcalendar/core/locales/es';
import * as moment from 'moment-timezone';

import { MatCalendar } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatNativeDateModule } from "@angular/material/core";
import { MatInputModule } from "@angular/material/input";
import { FormsModule } from "@angular/forms";
import { ObjectivesService } from "../../../core/services/objectives.service";
import { TestsService } from "../../../core/services/tests.service";
import { UsersService } from "../../../core/services/users.service";
import { LoaderService } from "../../../core/shared/services/loader.service";
import { SUBSCRIPTION_TYPES, TEST_TYPES, USER_TYPES } from "../../../core/shared/constants";
import { Activity, Objective } from "../../../core/models/objective.model";
import { AuthUser } from "../../../core/models/auth/auth.interface";
import { ProgressSummaryComponent } from "../../../core/shared/components/progress-summary/progress-summary.component";
import { TableComponent } from "../descubrete/objectives/table/table.component";
import { AppointmentsService } from "../../../core/services/appointments.service";
import { Appointment } from "../../../core/models/appointment.model";
import { environment } from "../../../../environments/environment";
import { VideoModalComponent } from "../video-modal/video-modal.component";
import { YouTubePlayerComponent } from "../../../core/shared/components/youtube-player/youtube-player.component";
import { WaitlistService } from "../../../core/services/waitlist.service";
import { EmailService, EmailData } from "../../../core/services/email.service";
import Swal from "sweetalert2";
import { SafePipe } from "../../../core/shared/pipes";

moment.tz.setDefault('America/Lima');

interface ObjetivoUI {
  _id: string;
  nombre: string;
  dimension: string;
  fin: string;
  porcentaje: number;
  activities?: ActividadUI[];
  state: string;
}

interface ActividadUI {
  nombre: string;
  descripcion: string;
  progreso: number;
  estado: string;
  inicio: string;
  fin: string;
}

interface Curso {
  id: string;
  titulo: string;
  vistas: number;
  duracion: number;
  imagen: string;
}

interface EntradaBlog {
  id: string;
  titulo: string;
  extracto: string;
  fecha: string;
}

@Component({
  selector: "app-dashboard-menu",
  standalone: true,
  imports: [
    CommonModule,
    FullCalendarModule,
    MatCalendar,
    MatFormFieldModule,
    MatNativeDateModule,
    MatInputModule,
    FormsModule,
    ProgressSummaryComponent,
    TableComponent,
    RouterModule,
    VideoModalComponent,
    YouTubePlayerComponent,
    SafePipe
  ],
  templateUrl: "./dashboard-executive.component.html",
  styleUrl: "./dashboard-executive.component.scss",
})
export class DashboardExecutiveComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService: AuthService = inject(AuthService);
  authUser: AuthUser = this.authService.getAuthUser()!;
  user: any = null; // Variable para los datos completos del usuario
  private objectivesService: ObjectivesService = inject(ObjectivesService);
  private testsService: TestsService = inject(TestsService);
  private loader = inject(LoaderService);
  private appointmentsService = inject(AppointmentsService);
  private waitlistService = inject(WaitlistService);
  private emailService = inject(EmailService);
  private usersService = inject(UsersService);

  // Calendario
  selectedDate: Date | null = null;
  calendarPlugins = [dayGridPlugin, timeGridPlugin, momentTimezonePlugin];
  currentWeekRange = this.getStartAndEndOfWeek();

  // Fecha actual
  currentDate: Date = new Date();

  // Datos del calendario
  timeSlots: string[] = ['7','8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22'];
  weekDays: any[] = [];
  miniCalendarDays: any[] = [];
  currentMonth: Date = new Date();
  currentMonthYear: string = '';
  
  // Variables para la vista del calendario
  currentView: 'week' | 'day' | 'month' = 'week'; // Se establecerá dinámicamente en ngOnInit
  selectedDayIndex: number = 0;
  selectedDay: any = null;
  currentWeekStart: Date = new Date();

  // Objetivos y progreso
  objetivos: ObjetivoUI[] = [];
  objetivosFiltrados: ObjetivoUI[] = []; // Array para los objetivos filtrados
  objetivosOriginales: Objective[] = [];
  totalPorcentajeObjetivos: number = 0;
  totalObjetivos: number = 0;
  totalObjetivosCompletados: number = 0;

  // Propiedades para el filtro
  estadosFiltro: string[] = ["Todos", "Nuevo", "Pendiente", "Empezado", "Completado"];
  estadoSeleccionado: string = "Todos";

  // Estadísticas de objetivos
  objectviesStarted: number = 0;
  objectivesPending: number = 0;
  objectivesFinished: number = 0;

  // Actividades
  actividades: ActividadUI[] = [];

  // Datos de test de autoconocimiento
  tests: any[] = [];
  testsCompleted: number = 0;
  dimensionsCompleted: number = 0;
  autoconocimientoProgress: number = 0;

  // Progreso de steps
  currentStep: number = 1; // Por defecto el primer step está activo

  // Array de steps del progreso
  // Configuración de distribución de semanas por step
  stepWeeksConfig = [
    { step: 1, title: 'Análisis profundo', weekStart: 1, weekEnd: 2 },
    { step: 2, title: 'Mapa de identidad', weekStart: 3, weekEnd: 3 },
    { step: 3, title: 'Phoenix Sessions', weekStart: 4, weekEnd: 4 },
    { step: 4, title: 'Visionboard', weekStart: 5, weekEnd: 5 },
    { step: 5, title: 'Phoenix Sessions', weekStart: 6, weekEnd: 6 },
    { step: 6, title: 'PRD 1', weekStart: 7, weekEnd: 9 },
    { step: 7, title: 'Plan de vida', weekStart: 10, weekEnd: 11 },
    { step: 8, title: 'Graduación', weekStart: 12, weekEnd: 15 }
  ];

  progressSteps: any[] = [
    {
      number: 1,
      title: 'Análisis profundo',
      status: 'pending',
      statusText: 'Sem 1 y 2',
      weeks: [],
      enabled: true
    },
    {
      number: 2,
      title: 'Mapa de identidad',
      status: 'pending',
      statusText: 'Sem 3',
      weeks: [],
      enabled: true
    },
    {
      number: 3,
      title: 'Phoenix Sessions',
      status: 'pending',
      statusText: 'Sem 4',
      weeks: [],
      enabled: true
    },
    {
      number: 4,
      title: 'Visionboard',
      status: 'pending',
      statusText: 'Sem 5',
      weeks: [],
      enabled: true
    },
    {
      number: 5,
      title: 'Phoenix Sessions',
      status: 'pending',
      statusText: 'Sem 6',
      weeks: [],
      enabled: true
    },
    {
      number: 6,
      title: 'PRD 1',
      status: 'pending',
      statusText: 'Sem 7, 8 y 9',
      weeks: [],
      enabled: true
    },
    {
      number: 7,
      title: 'Plan de vida',
      status: 'pending',
      statusText: 'Sem 10 y 11',
      weeks: [],
      enabled: true
    },
    {
      number: 8,
      title: 'Graduación',
      status: 'pending',
      statusText: 'Sem 12 a 15',
      weeks: [],
      enabled: true
    }
  ];

  // Secciones de progreso
  progresoOnboarding: boolean = false;
  progresoDescubrete: boolean = false;
  progresoPRD: boolean = false;
  progresoRevision: boolean = false;

  // Mentores y expertos
  horasMentores: number = 0;
  horasExpertos: number = 0;

  horasTotalesMentores: number = 0;
  horasTotalesExpertos: number = 0;

  // Academy y blog
  cursos: Curso[] = [];
  blog: EntradaBlog[] = [];

  // Configuración del calendario
  calendarOptions: any;

  // Estados para manejar mensajes informativos
  messageHide: boolean = true;

  // Fecha actual formateada
  dateToday: string = new Date().toLocaleDateString("es-ES", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  cards = [
    {
      title: "Objetivo",
      subtitle: "Resumen",
      progressType: "spinner",
      progressValue: 0,
    },
    {
      title: "Mentores",
      subtitle: "24h en tu Plan",
      progressType: "bar",
      progressValue: 75,
    },
    {
      title: "Expertos",
      subtitle: "24h en tu Plan",
      progressType: "bar",
      progressValue: 5,
    },
  ];

  userAppointments: Appointment[] = [];
  eventsByDay: any[] = [];

  // Propiedades para el cálculo de semana actual
  private _currentWeek: number | null = null;

  // Propiedades para el task-card de próxima cita
  nextAppointment: Appointment | null = null;
  daysUntilAppointment: number = 0;
  countdownTime: string = '';
  showAppointmentCard: boolean = false;
  private countdownInterval: any = null;

  /**
   * Limpiar caché de la semana actual
   */
  clearCurrentWeekCache(): void {
    this._currentWeek = null;
    // También limpiar caché del card de mentor cuando se limpia la semana
    this._shouldShowMentorCard = null;
  }

  getCurrentWeek(): number {
    // Si ya se calculó, devolver el valor en caché
    if (this._currentWeek !== null) {
      return this._currentWeek;
    }

    console.log("getCurrentWeek - user:", this.user);
    console.log("getCurrentWeek - user.createdAt:", this.user?.createdAt);
    
    if (!this.user?.createdAt) {
      console.log("getCurrentWeek - No createdAt found, returning 1 as default");
      this._currentWeek = 1;
      return this._currentWeek;
    }

    const createdAt = new Date(this.user.createdAt);
    const now = new Date();
    
    console.log("getCurrentWeek - createdAt date:", createdAt);
    console.log("getCurrentWeek - now date:", now);
    
    // Normalizar ambas fechas al inicio del día en zona local
    const startOfCreatedAt = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calcular la diferencia en milisegundos
    const diffInMs = Math.max(0, startOfToday.getTime() - startOfCreatedAt.getTime());
    
    // Convertir a días naturales transcurridos
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    console.log("getCurrentWeek - diffInDays:", diffInDays);
    
    // Calcular la semana asegurando que el día 1 pertenezca a la semana 1
    const week = Math.max(1, Math.floor(diffInDays / 7) + 1);
    
    console.log("getCurrentWeek - calculated week:", week);
    
    // Guardar en caché
    this._currentWeek = week;
    return this._currentWeek;
  }

  initializeProgressSteps(): void {
    // Limpiar caché para recalcular si es necesario
    this.clearCurrentWeekCache();
    const currentWeek = this.getCurrentWeek();
    console.log("currentWeek", currentWeek);
    const step1Complete = this.user?.stepsAnswers?.step1?.step1Complete === true;
    const step2Complete = this.user?.stepsAnswers?.step2?.step2Complete === true;
    const step3Complete = this.user?.stepsAnswers?.step5?.phoenixSessions3Completed === true;
    const step4Complete = this.user?.stepsAnswers?.step3?.VisionBoardComplete === true;
    const step5Complete = this.user?.stepsAnswers?.step5?.phoenixSessions5Completed === true;
    const step6Complete = this.user?.stepsAnswers?.step4?.rankingCompleted === true;
    const step7Complete = this.user?.stepsAnswers?.step5?.phoenixSessions7Completed === true;
    const step8Complete = this.user?.stepsAnswers?.step5?.phoenixSessions8Completed === true;
    const step9Complete = this.user?.stepsAnswers?.step5?.phoenixSessions9Completed === true;
    
    // Helper function to determine step status
    const getStepStatus = (stepComplete: boolean, weekStart: number, weekEnd: number, stepTitle: string) => {
      const adjustedWeekStart = Math.max(1, weekStart);
      const adjustedWeekEnd = Math.max(adjustedWeekStart, weekEnd);

      const isInRange = currentWeek >= adjustedWeekStart && currentWeek <= adjustedWeekEnd;
      const isAfterRange = currentWeek > adjustedWeekEnd;

      const formatRange = (start: number, end: number) =>
        `Sem ${start}${start !== end ? ` y ${end}` : ''}`;
      
      if (stepComplete && isInRange) {
        return { status: 'completed', statusText: `Culminado - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      } else if (stepComplete && isAfterRange) {
        return { status: 'completed', statusText: `Completado - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      } else if (!stepComplete && isInRange) {
        return { status: 'in-progress', statusText: `En curso - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      } else {
        return { status: 'pending', statusText: `Pendiente - ${formatRange(adjustedWeekStart, adjustedWeekEnd)}` };
      }
    };
    
    this.progressSteps = [
      {
        number: 1,
        title: 'Análisis profundo',
        ...getStepStatus(step1Complete, 1, 2, 'Análisis profundo')
      },
      {
        number: 2,
        title: 'Mapa de identidad',
        ...getStepStatus(step2Complete, 3, 3, 'Mapa de identidad')
      },
      {
        number: 3,
        title: 'Phoenix Session',
        ...getStepStatus(step3Complete, 4, 4, 'Phoenix Session')
      },
      {
        number: 4,
        title: 'Visionboard',
        ...getStepStatus(step4Complete, 5, 5, 'Visionboard')
      },
      {
        number: 5,
        title: 'Phoenix Session',
        ...getStepStatus(step5Complete, 6, 6, 'Phoenix Session')
      },
      {
        number: 6,
        title: 'PRD',
        ...getStepStatus(step6Complete, 7, 9, 'PRD')
      },
      {
        number: 7,
        title: 'Plan de vida',
        ...getStepStatus(step7Complete, 10, 11, 'Plan de vida')
      },
      {
        number: 8,
        title: 'Graduación',
        ...getStepStatus(step8Complete, 12, 12, 'Graduación')
      }
    ];
  }

  // Modal para preselección Executive
  showExecutiveModal: boolean = false;
  
  // Modal para detalles de cita
  showAppointmentModal: boolean = false;
  selectedAppointment: Appointment | null = null;
  
  // Propiedad cached para el card de mentor (evita recálculos repetitivos)
  private _shouldShowMentorCard: boolean | null = null;
  
  // Valores generados para el modal de cita (se generan una sola vez)
  private meetingId: string = '';
  private accessCode: string = '';
  private meetingLink: string = '';
  
  // Control del sidebar móvil
  showMobileSidebar: boolean = false;
  
  // Propiedad para almacenar el estado Freemium (evita bucles)
  private _isUserFreemium: boolean = false;
  
  // Propiedades para almacenar estados del calendario (evitan bucles)
  private _isTodaySelected: boolean = false;
  private _isDayViewActive: boolean = false;
  private _isNavigatingMonth: boolean = false;
  
  // Variable para mostrar el banner de felicitaciones por salario alto
  showSalaryBanner: boolean = false;
  isHighSalaryUser: boolean = false;
  
  // Banner de Tip Phoenix (Agendar con experto)
  showTipPhoenixBanner: boolean = false;
  
  // Banner de Phoenix Sessions
  showPhoenixSessionsBanner: boolean = false;

  // Banner de Graduación
  showGraduationBanner: boolean = false;

  // Banner de Análisis Profundo
  showDeepAnalysisBanner: boolean = false;
  deepAnalysisDeadlineDate: string = '';

  // Modal de lista de espera
  showWaitlistModal: boolean = false;
  isSubmittingWaitlist: boolean = false;
  waitlistForm = {
    nombre: '',
    apellido: '',
    telefono: '',
    correoElectronico: '',
    provincia: '',
    distrito: ''
  };

  // Provincias y distritos para el formulario de lista de espera
  provincias = [
    'Amazonas', 'Ancash', 'Apurímac', 'Arequipa', 'Ayacucho', 'Cajamarca',
    'Callao', 'Cusco', 'Huancavelica', 'Huánuco', 'Ica', 'Junín', 'La Libertad',
    'Lambayeque', 'Lima', 'Loreto', 'Madre de Dios', 'Moquegua', 'Pasco',
    'Piura', 'Puno', 'San Martín', 'Tacna', 'Tumbes', 'Ucayali'
  ];

  distritos: { [key: string]: string[] } = {
    'Lima': ['Ancón', 'Ate', 'Barranco', 'Breña', 'Carabayllo', 'Chaclacayo', 'Chorrillos', 'Cieneguilla', 'Comas', 'El Agustino', 'Independencia', 'Jesús María', 'La Molina', 'La Victoria', 'Lince', 'Los Olivos', 'Lurigancho', 'Lurín', 'Magdalena del Mar', 'Miraflores', 'Pachacámac', 'Pucusana', 'Pueblo Libre', 'Puente Piedra', 'Punta Hermosa', 'Punta Negra', 'Rímac', 'San Bartolo', 'San Borja', 'San Isidro', 'San Juan de Lurigancho', 'San Juan de Miraflores', 'San Luis', 'San Martín de Porres', 'San Miguel', 'Santa Anita', 'Santa María del Mar', 'Santa Rosa', 'Santiago de Surco', 'Surquillo', 'Villa El Salvador', 'Villa María del Triunfo'],
    'Arequipa': ['Arequipa', 'Alto Selva Alegre', 'Cayma', 'Cerro Colorado', 'Characato', 'Chiguata', 'Jacobo Hunter', 'La Joya', 'Mariano Melgar', 'Miraflores', 'Mollebaya', 'Paucarpata', 'Pocsi', 'Polobaya', 'Quequeña', 'Sabandía', 'Sachaca', 'San Juan de Siguas', 'San Juan de Tarucani', 'Santa Isabel de Siguas', 'Santa Rita de Siguas', 'Socabaya', 'Tiabaya', 'Uchumayo', 'Vitor', 'Yanahuara', 'Yarabamba', 'Yura'],
    'Cusco': ['Cusco', 'Ccorca', 'Poroy', 'San Jerónimo', 'San Sebastian', 'Santiago', 'Saylla', 'Wanchaq'],
    'Piura': ['Piura', 'Castilla', 'Catacaos', 'Cura Mori', 'El Tallan', 'La Arena', 'La Unión', 'Las Lomas', 'Tambo Grande', 'Veintiséis de Octubre'],
    'La Libertad': ['Trujillo', 'El Porvenir', 'Florencia de Mora', 'Huanchaco', 'La Esperanza', 'Laredo', 'Moche', 'Poroto', 'Salaverry', 'Simbal', 'Victor Larco Herrera'],
    'Lambayeque': ['Chiclayo', 'Chongoyape', 'Eten', 'Eten Puerto', 'José Leonardo Ortiz', 'La Victoria', 'Lagunas', 'Monsefú', 'Nueva Arica', 'Oyotún', 'Picsi', 'Pimentel', 'Reque', 'San José', 'Santa Rosa', 'Saña', 'Cayalti', 'Pátapo', 'Pomalca', 'Pucalá', 'Tuman'],
    'Junín': ['Huancayo', 'Chacapampa', 'Chanchamayo', 'Chicche', 'Chilca', 'Chongos Alto', 'Chupaca', 'Colca', 'Cullhuas', 'El Tambo', 'Huacrapuquio', 'Hualhuas', 'Huancan', 'Huasicancha', 'Huayucachi', 'Ingenio', 'Pariahuanca', 'Pilcomayo', 'Pucara', 'Quichuay', 'Quilcas', 'San Agustín', 'San Jerónimo de Tunan', 'Saño', 'Sapallanga', 'Sicaya', 'Santo Domingo de Acobamba', 'Viques'],
    'Callao': ['Callao', 'Bellavista', 'Carmen de la Legua Reynoso', 'La Perla', 'La Punta', 'Ventanilla', 'Mi Perú'],
    'Ancash': ['Huaraz', 'Cochabamba', 'Colcabamba', 'Huanchay', 'Independencia', 'Jangas', 'La Libertad', 'Olleros', 'Pampas Grande', 'Pariacoto', 'Pira', 'Tarica'],
    'Ica': ['Ica', 'La Tinguiña', 'Los Aquijes', 'Ocucaje', 'Pachacutec', 'Parcona', 'Pueblo Nuevo', 'Salas', 'San José de Los Molinos', 'San Juan Bautista', 'Santiago', 'Subtanjalla', 'Tate', 'Yauca del Rosario'],
    'Tacna': ['Tacna', 'Alto de la Alianza', 'Calana', 'Ciudad Nueva', 'Inclan', 'Pachia', 'Palca', 'Pocollay', 'Sama', 'Coronel Gregorio Albarracín Lanchipa'],
    'Puno': ['Puno', 'Acora', 'Amantani', 'Atuncolla', 'Capachica', 'Chucuito', 'Coata', 'Huata', 'Mañazo', 'Paucarcolla', 'Pichacani', 'Plateria', 'San Antonio', 'Tiquillaca', 'Vilque'],
    'Cajamarca': ['Cajamarca', 'Asunción', 'Cospan', 'Encañada', 'Jesús', 'Llacanora', 'Los Baños del Inca', 'Magdalena', 'Matara', 'Namora', 'San Juan'],
    'Ayacucho': ['Huamanga', 'Acos Vinchos', 'Andrés Avelino Cáceres Dorregaray', 'Ayacucho', 'Carmen Alto', 'Chiara', 'Ocros', 'Pacaycasa', 'Quinua', 'San José de Ticllas', 'San Juan Bautista', 'Santiago de Pischa', 'Socos', 'Tambillo', 'Vinchos'],
    'Huancavelica': ['Huancavelica', 'Acobambilla', 'Acoria', 'Conayca', 'Cuenca', 'Huachocolpa', 'Huayllahuara', 'Laria', 'Manta', 'Mariscal Cáceres', 'Moya', 'Nuevo Occoro', 'Palca', 'Pilchaca', 'Vilca', 'Yauli'],
    'Huánuco': ['Huánuco', 'Amarilis', 'Chinchao', 'Churubamba', 'Margos', 'Quisqui', 'San Francisco de Cayran', 'San Pedro de Chaulan', 'Santa María del Valle', 'Yarumayo', 'Pillco Marca', 'Yacus'],
    'Loreto': ['Maynas', 'Alto Nanay', 'Fernando Lores', 'Indiana', 'Las Amazonas', 'Mazán', 'Napo', 'Punchana', 'Putumayo', 'Torres Causana', 'Belén', 'San Juan Bautista', 'Teniente Manuel Clavero'],
    'Ucayali': ['Coronel Portillo', 'Callería', 'Campoverde', 'Iparía', 'Masisea', 'Yarinacocha', 'Nueva Requena'],
    'San Martín': ['Moyobamba', 'Calzada', 'Habana', 'Jepelacio', 'Soritor', 'Yantalo'],
    'Madre de Dios': ['Tambopata', 'Inambari', 'Las Piedras', 'Laberinto'],
    'Moquegua': ['Moquegua', 'Carumas', 'Cuchumbaya', 'Omate', 'Quinistaquillas', 'Samegua'],
    'Pasco': ['Cerro de Pasco', 'Chaupimarca', 'Huachon', 'Huariaca', 'Huayllay', 'Ninacaca', 'Pallanchacra', 'Paucartambo', 'San Francisco de Asis de Yarusyacan', 'Simon Bolivar', 'Ticlacayan', 'Tinyahuarco', 'Vicco', 'Yanacancha'],
    'Apurímac': ['Abancay', 'Chacoche', 'Circa', 'Curahuasi', 'Huanipaca', 'Lambrama', 'Pichirhua', 'San Juan de Chacña', 'Tamburco'],
    'Amazonas': ['Chachapoyas', 'Asunción', 'Balsas', 'Cheto', 'Chiliquin', 'Chuquibamba', 'Granada', 'Huancas', 'La Jalca', 'Leimebamba', 'Levanto', 'Magdalena', 'Mariscal Castilla', 'Molinopampa', 'Montevideo', 'Olleros', 'Quinjalca', 'San Francisco de Daguas', 'San Isidro de Maino', 'Soloco', 'Sonche'],
    'Tumbes': ['Tumbes', 'Corrales', 'La Cruz', 'Pampas de Hospital', 'San Jacinto', 'San Juan de la Virgen', 'Zorritos']
  };

  // Propiedades para el botón flotante y modal de video
  showVideoModal: boolean = false;
  shouldAutoPlayVideo: boolean = false;
  
  // ViewChild para el reproductor de YouTube
  @ViewChild('youtubePlayer', { static: false }) youtubePlayer!: ElementRef;

  constructor(private router: Router) { }
  ngAfterViewInit(): void {

    this.loader.show();

    // Realizamos múltiples llamadas en paralelo
    Promise.all([
      // Primero, cargamos los datos completos del usuario
      new Promise<void>((resolve) => {
        this.authService.findUserById(this.authUser.id).subscribe({
          next: (userResponse) => {
            if (userResponse && userResponse.data) {
              this.user = userResponse.data;
              // Cargar datos del usuario después de asignar el usuario
                this.loadUserData();
                this.loadAppointments();
                this.updateCalendarStates();
                // Ahora que this.user está disponible, verificamos si debe reproducir el video
                this.checkAutoPlayVideo();
            }
            resolve();
          },
          error: (error) => {
            console.error("Error al cargar datos del usuario:", error);
            resolve();
          },
        });
      }),

      // Cargamos los objetivos
      new Promise<void>((resolve) => {
        this.objectivesService.findAll(this.authUser.id).subscribe({
          next: (response) => {
            console.log("Respuesta de objetivos:", response);

            if (response && response.data && Array.isArray(response.data)) {
              this.objetivosOriginales = response.data;
              this.procesarObjetivos();
            }
            resolve();
          },
          error: (error) => {
            console.error("Error al cargar objetivos:", error);
            resolve();
          },
        });
      }),

      // Cargamos los tests de autoconocimiento
      new Promise<void>((resolve) => {
        this.testsService
          .find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
          .subscribe({
            next: (response) => {
              console.log("Respuesta de tests:", response);

              if (response && response.data) {
                this.tests = response.data;
                this.calcularProgresoAutoconocimiento();
              }
              resolve();
            },
            error: (error) => {
              console.error("Error al cargar tests:", error);
              resolve();
            },
          });
      }),
    ]).finally(() => {
      this.loader.hide();
    });
  }

  setupCalendarOptions() {

    console.log('currentWeekRange',this.currentWeekRange);

    const events = this.userAppointments.map(x => ({ title: x.coach.name, date: x.dateValue }));
    const isMobile = window.innerWidth < 768;

    this.calendarOptions = {
      timeZone:"America/Lima",
      locale: esLocale, 
      initialView: isMobile ? "timeGridDay" : "timeGridWeek",
      weekends: true,
      plugins: this.calendarPlugins,
      events: events || [],
      headerToolbar: isMobile
        ? {
          left: "prev,next title",
          center: "",
          right: "today",
        }
        : {
          left: "prev,next title",
          center: "",
          right: "timeGridWeek,timeGridDay today",
        },
      slotMinTime: "07:00:00",
      slotMaxTime: "22:00:00",
      slotDuration: "00:30:00",
      nowIndicator: true,
      validRange: {
        // Kept from original, defines the navigable range
        start: this.currentWeekRange.start,
        end: this.currentWeekRange.end,
      },
      firstDay: 0,
      initialDate: this.currentWeekRange.start, // Start view at the beginning of the current week
      allDaySlot: false,
      height: "auto", // Let the container's CSS dictate height
      handleWindowResize: true, // Enable FullCalendar's internal resize handling
      windowResizeDelay: 200, // Debounce resize events
      windowResize: (arg: any) => {
        const currentIsMobile = window.innerWidth < 768;
        if (currentIsMobile) {
          if (!arg.calendar) return
          if (arg.calendar.view.type !== "timeGridDay") {
            arg.calendar.changeView("timeGridDay");
          }
          arg.calendar.setOption("headerToolbar", {
            left: "prev,next title",
            center: "",
            right: "today",
          });
        } else {
          // Desktop
          if (arg.calendar.view.type !== "timeGridWeek") {
            arg.calendar.changeView("timeGridWeek");
          }
          arg.calendar.setOption("headerToolbar", {
            left: "prev,next title",
            center: "",
            right: "timeGridWeek,timeGridDay today",
          });
        }
      },
    };
  }

  ngOnInit() {
    this.setupCalendarOptions();
    this.initializeWeekDays();
    this.initializeMiniCalendar();
    
    // Establecer vista por defecto basada en el tamaño de pantalla
    this.setDefaultView();
    
    // Agregar listener para redimensionamiento de ventana
    window.addEventListener('resize', () => {
      this.setDefaultView();
    });
    
    // No llamamos checkAutoPlayVideo aquí porque this.user aún no está disponible
    // Se llamará después de cargar los datos del usuario
  }

  // Establecer vista por defecto basada en el tamaño de pantalla
  setDefaultView() {
    if (window.innerWidth <= 768) {
      // En móvil, vista por defecto es 'week'
      this.currentView = 'week';
    } else {
      // En desktop, vista por defecto es 'month'
      this.currentView = 'month';
    }
  }

  // Cálculo avanzado del progreso de autoconocimiento
  calcularProgresoAutoconocimiento() {
    this.testsCompleted = 0;
    this.dimensionsCompleted = 0;
    let dimensionsTotalCount = 0;

    // Analizar cada test y sus dimensiones
    this.tests.forEach((test: any) => {
      if (test.completed) this.testsCompleted++;

      if (test.dimensions && Array.isArray(test.dimensions)) {
        // Contar todas las dimensiones
        dimensionsTotalCount += test.dimensions.length;

        // Contar las dimensiones completadas
        test.dimensions.forEach((dimension: any) => {
          if (dimension.completed) {
            this.dimensionsCompleted++;
          }
        });
      }
    });

    // Calculamos el porcentaje general de progreso de autoconocimiento
    this.autoconocimientoProgress =
      dimensionsTotalCount > 0
        ? Math.round((this.dimensionsCompleted / dimensionsTotalCount) * 100)
        : 0;

    // Actualizamos el estado de la sección "Descúbrete" en el dashboard
    // Si al menos una dimensión está completada, se considera en progreso
    this.progresoDescubrete = this.dimensionsCompleted > 0;

    // También actualizamos el PRD si hay suficiente progreso en autoconocimiento
    // Por ejemplo, si han completado al menos el 50% de las dimensiones
    this.progresoPRD = this.autoconocimientoProgress >= 50;

    console.log(
      `Progreso de autoconocimiento: ${this.autoconocimientoProgress}% (${this.dimensionsCompleted}/${dimensionsTotalCount} dimensiones completadas)`
    );
  }

  // Cargar datos básicos del usuario
  loadUserData() {
    // Debug temporal para ver la estructura del usuario
    console.log('=== LOAD USER DATA ===');
    console.log('Usuario completo:', this.user);
    console.log('Tipo de usuario:', this.user?.type);
    console.log('Tipo de suscripción:', this.user?.type?.suscriptionType);
    console.log('ID de suscripción:', this.user?.type?.suscriptionType?.id);
    console.log('Nombre de suscripción:', this.user?.type?.suscriptionType?.name);
    console.log('CronWeek:', this.user?.type?.CronWeek);
    console.log('=== FIN LOAD USER DATA ===');
    
    // Verificar si el onboarding está completo (usando el objeto user completo)
    if (
      this.user &&
      this.user.profile &&
      this.user.profile.onboardingComplete
    ) {
      this.progresoOnboarding = true;
    }

    // NOTA: checkExecutiveEligibility() se llama después de cargar las citas en loadAppointments()
    // para poder verificar si el usuario ya tuvo una cita con su mentor
    
    // Verificar si debe mostrarse el banner de Phoenix Sessions
    this.checkPhoenixSessionsBanner();
    
    // Verificar si debe mostrarse el banner de Graduación
    this.checkGraduationBanner();
    
    // Verificar si debe mostrarse el banner de Tip Phoenix (Agendar con experto)
    this.checkTipPhoenixBanner();
    
    // Verificar si debe mostrarse el banner de Análisis Profundo
    this.checkDeepAnalysisBanner();

    // Verificar si ya se completó el formulario de lista de espera en semana 12
    this.checkWaitlistFormCompleted();

    // Comprobar si el usuario es Freemium
    this.updateFreemiumStatus();

    // Para usuarios Freemium, mostrar horas en 0 según el requerimiento
    if (this.isUserFreemium()) {
      this.horasMentores = 0;
      this.horasExpertos = 0;
      this.horasTotalesMentores = 0;
      this.horasTotalesExpertos = 0;
    } else {
      // Para usuarios no Freemium, mostrar los valores reales o predeterminados
      this.horasMentores = 0;
      this.horasExpertos = 0;
      this.horasTotalesMentores = this.user?.type?.subscription?.coachHoursTotal || 0;
      this.horasTotalesExpertos = this.user?.type?.subscription?.expertHoursTotal || 0;
    }

    // Inicializar los steps de progreso basados en la semana actual
    this.initializeProgressSteps();
    
    // Actualizar los steps basado en el progreso del usuario (ya no modifica estados)
    this.updateStepsBasedOnProgress();
    
    // IMPORTANTE: Mapear CronWeek a progressSteps AL FINAL para que prevalezca sobre cualquier otra lógica
    this.mapCronWeekToSteps();
  }

  ngOnDestroy(): void {
    // Limpiar intervalo de cuenta regresiva
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  loadAppointments() {
    if (!this.user?._id) {
      console.warn('Usuario no disponible para cargar citas');
      return;
    }

    this.appointmentsService.findByCustomer(this.user._id).subscribe({
      next: (res) => {
        this.userAppointments = res.data;
        
        // Limpiar caché cuando se cargan nuevas citas
        this._shouldShowMentorCard = null;
        
        // Calcular próxima cita y días faltantes
        this.calculateNextAppointment();
        
        // Actualizar la lista de eventos agrupados por día
        this.updateEventsByDay();
        
        this.setupCalendarOptions();
        this.updateMiniCalendar(); // Actualizar el calendario mensual con las nuevas citas
        
        // Verificar si debe mostrarse el modal Executive después de cargar las citas
        this.checkExecutiveEligibility();
      },
      error: (err) => {
        console.error('Error al cargar citas:', err);
        // Limpiar caché incluso si hay error
        this._shouldShowMentorCard = null;
        // Aún así verificar el modal aunque falle la carga de citas
        this.checkExecutiveEligibility();
      }
    })
  }

  // Método simplificado para procesar objetivos
  procesarObjetivos() {
    // Si el usuario es Freemium, establecer porcentaje total en 0
    if (this.isUserFreemium()) {
      this.totalPorcentajeObjetivos = 0;
      this.totalObjetivos = 0;
      this.totalObjetivosCompletados = 0;
      this.objetivos = [];
      this.objetivosFiltrados = [];
      return;
    }

    if (!this.objetivosOriginales || this.objetivosOriginales.length === 0) {
      console.log("No hay objetivos para mostrar");
      this.objetivos = [];
      this.objetivosFiltrados = [];
      return;
    }

    // Mapear objetivos para la UI
    this.objetivos = this.objetivosOriginales.map((obj: any) => ({
      _id: obj._id || "",
      nombre: obj.name || "",
      dimension: obj.dimension?.name || "General",
      fin: this.formatDate(obj.end) || "Sin fecha",
      porcentaje: obj.percentage || 0,
      activities: this.procesarActividades(obj.activities),
      state: obj.state
    }));

    // Extraer actividades
    this.extraerActividades();

    // Aplicar filtro inicial
    this.filtrarObjetivos();

    // Actualizar porcentaje total y estadísticas
    if (this.objetivos.length > 0) {
      this.totalObjetivos = this.objetivos.length;
      this.totalObjetivosCompletados = this.objetivos.filter(obj => 
        obj.activities && obj.activities.every(act => act.estado === 'Completado')
      ).length;
      
      this.totalPorcentajeObjetivos = Math.round(
        this.objetivos.reduce((sum, obj) => sum + obj.porcentaje, 0) /
        this.objetivos.length
      );
      this.cards[0].progressValue = this.totalPorcentajeObjetivos;
    } else {
      this.totalObjetivos = 0;
      this.totalObjetivosCompletados = 0;
    }
  }

  // Nuevo método para filtrar objetivos por estado
  filtrarObjetivos() {
    if (this.estadoSeleccionado === "Todos") {
      this.objetivosFiltrados = [...this.objetivos];
    } else {
      this.objetivosFiltrados = this.objetivos.filter(
        (objetivo) =>
          objetivo.activities &&
          objetivo.activities.some(
            (actividad) => actividad.estado === this.estadoSeleccionado
          )
      );
    }
  }

  // Método simplificado para procesar actividades
  procesarActividades(activities?: any[]): ActividadUI[] {
    if (!activities || !Array.isArray(activities) || activities.length === 0) {
      return [];
    }

    return activities.map((act) => ({
      nombre: act.name || "",
      descripcion: act.description || "",
      progreso: this.calcularProgresoActividad(act),
      estado: this.obtenerEstadoActividad(act),
      inicio: act.start ? this.formatDate(act.start) : "Sin fecha",
      fin: act.end ? this.formatDate(act.end) : "Sin fecha",
    }));
  }

  // Simplificar cálculo de progreso
  calcularProgresoActividad(actividad: any): number {
    if (!actividad || !actividad.state) return 0;

    switch (actividad.state) {
      case "Terminado":
      case "Realizado":
      case "Completada":
      case "Completado":
        return 100;
      case "Empezado":
      case "En progreso":
        return 50;
      case "Pendiente":
      case "Nuevo":
      case "No empezado":
      default:
        return 0;
    }
  }

  // Simplificar obtención de estado
  obtenerEstadoActividad(actividad: any): string {
    if (!actividad || !actividad.state) return "Pendiente";

    switch (actividad.state) {
      case "Empezado":
        return "En progreso";
      case "Terminado":
      case "Realizado":
      case "Completado":
        return "Completada";
      case "No empezado":
        return "Pendiente";
      default:
        return actividad.state;
    }
  }

  // Extraer actividades de objetivos
  extraerActividades() {
    this.actividades = [];

    this.objetivos.forEach((objetivo) => {
      if (objetivo.activities && objetivo.activities.length > 0) {
        this.actividades.push(...objetivo.activities);
      }
    });
  }

  // Método para formatear fecha (como en ObjectivesComponent)
  formatDate(dateStr: string): string {
    if (!dateStr || dateStr.trim() === "") return "Sin fecha";

    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "Sin fecha"; // Fecha inválida

      // Si la fecha es hoy o mañana
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      if (date.toDateString() === today.toDateString()) {
        return "Hoy";
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return "Mañana";
      } else {
        // Formato DD/MM/YYYY
        return `${date.getDate().toString().padStart(2, "0")}/${(
          date.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`;
      }
    } catch (error) {
      console.error("Error al formatear fecha:", error);
      return "Sin fecha";
    }
  }

  // Método para obtener el rango de la semana actual
  getStartAndEndOfWeek(): { start: string; end: string } {
    const now = new Date();
    const dayOfWeek = now.getDay();

    console.log('now.getDay()',now.getDay());
    console.log('now.getUTCDay()',now.getUTCDay());

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    console.log('startOfWeek', startOfWeek);
    console.log('endOfWeek', endOfWeek);

    return {
      start: startOfWeek.toISOString().split("T")[0],
      end: endOfWeek.toISOString().split("T")[0],
    };
  }

  // Ocultar mensaje
  hideMessage() {
    this.messageHide = true;
  }

  // Método para manejar el cambio de fecha en el calendario
  onDateChange(date: Date) {
    console.log("Fecha seleccionada:", date);
  }

  // Navegar a la página de objetivos
  verTodosObjetivos() {
    this.router.navigate(["dashboard/objectives"]);
  }

  // Navegar a la página de autoconocimiento
  verAutoconocimiento() {
    // Solo permitir navegación para usuarios no Freemium
    if (this.isUserFreemium()) {
      return;
    }

    this.router.navigate(["dashboard/life-circle"]);
  }

  // Navegar a la página para crear un nuevo objetivo
  crearNuevoObjetivo() {
    console.log("Creando nuevo objetivo...");
    // Si el usuario es Freemium, no permitir la creación de objetivos
    if (this.isUserFreemium()) {
      //return;
    }

    this.router.navigate(["dashboard/new-objective"], {
      state: { objetivo: null, editable: true },
    });
  }

  // Navegar a la página PRD
  toPRD() {
    // Solo permitir navegación para usuarios no Freemium
    if (this.isUserFreemium()) {
      return;
    }

    this.router.navigate(["dashboard/prd"]);
  }

  // Navegar al Círculo de Vida (disponible para todos, incluso Freemium)
  toCircleOfLife() {
    this.router.navigate(["dashboard/life-map"]);
  }

  // Método para verificar si el usuario es Freemium
  isUserFreemium(): boolean {
    return this._isUserFreemium;
  }
  
  // Método privado para actualizar el estado Freemium
  private updateFreemiumStatus(): void {
    console.log('=== DEBUG updateFreemiumStatus ===');
    console.log('this.user:', this.user);
    console.log('this.user?.type:', this.user?.type);
    console.log('this.user?.type?.suscriptionType:', this.user?.type?.suscriptionType);
    console.log('SUBSCRIPTION_TYPES.Freemium.id:', SUBSCRIPTION_TYPES.Freemium.id);
    
    // Buscar en diferentes ubicaciones posibles
    const suscriptionType1 = this.user?.type?.suscriptionType;
    const suscriptionType2 = this.user?.suscriptionType;
    const suscriptionType3 = this.user?.subscription;
    
    console.log('suscriptionType1 (user.type.suscriptionType):', suscriptionType1);
    console.log('suscriptionType2 (user.suscriptionType):', suscriptionType2);
    console.log('suscriptionType3 (user.subscription):', suscriptionType3);
    
    this._isUserFreemium = !!(
      this.user &&
      this.user.type &&
      this.user.type.suscriptionType &&
      (this.user.type.suscriptionType.id == SUBSCRIPTION_TYPES.Freemium.id)
    );
    
    console.log('Resultado _isUserFreemium:', this._isUserFreemium);
    console.log('=== FIN DEBUG ===');
  }

  // Método para manejar el cambio de filtro desde el componente
  onFiltroChange(estado: string) {
    this.estadoSeleccionado = estado;
    this.filtrarObjetivos();
  }

  // Métodos para el calendario
  initializeWeekDays() {
    this.updateWeekDays();
    this.selectedDay = this.weekDays[0];
  }

  updateWeekDays() {
    const daysOfWeek = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    this.weekDays = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(this.currentWeekStart);
      date.setDate(this.currentWeekStart.getDate() + i);
      
      this.weekDays.push({
        name: daysOfWeek[i],
        date: `${date.getDate()}/${date.getMonth() + 1}`,
        fullDate: date
      });
    }
  }

  getCurrentWeekRange(): string {
    if (this.weekDays.length === 0) return '';
    
    const startDate = this.weekDays[0].fullDate;
    const endDate = this.weekDays[6].fullDate;
    
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const month = months[startDate.getMonth()];
    const year = startDate.getFullYear();
    
    return `${startDay} - ${endDay} ${month} ${year}`;
  }

  navigateWeek(direction: number) {
    this.currentWeekStart.setDate(this.currentWeekStart.getDate() + (direction * 7));
    this.updateWeekDays();
    
    // Si estamos en vista diaria, mantener el mismo día de la semana seleccionado
    if (this.currentView === 'day' && this.selectedDayIndex < this.weekDays.length) {
      this.selectedDay = this.weekDays[this.selectedDayIndex];
    }
    
    // Actualizar estados del calendario
    this.updateCalendarStates();
  }

  navigateDay(direction: number) {
    const newIndex = this.selectedDayIndex + direction;
    if (newIndex >= 0 && newIndex < this.weekDays.length) {
      this.selectedDayIndex = newIndex;
      this.selectedDay = this.weekDays[newIndex];
      this.updateCalendarStates();
    }
  }

  changeView(view: 'week' | 'day') {
    this.currentView = view;
    if (view === 'day') {
      this.selectedDay = this.weekDays[this.selectedDayIndex];
    }
    this.updateCalendarStates();
  }

  goToToday() {
    console.log('goToToday: ejecutándose...');
    
    const today = new Date();
    const todayDayOfWeek = today.getDay(); // 0 = domingo, 1 = lunes, etc.
    
    console.log('goToToday: día de hoy es', todayDayOfWeek, '(', ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][todayDayOfWeek], ')');
    
    // Calcular el inicio de la semana actual
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - todayDayOfWeek);
    
    console.log('goToToday: inicio de semana calculado:', startOfWeek);
    console.log('goToToday: semana actual mostrada:', this.currentWeekStart);
    
    // Si la semana actual es diferente a la mostrada, actualizar
    if (startOfWeek.getTime() !== this.currentWeekStart.getTime()) {
      console.log('goToToday: actualizando semana...');
      this.currentWeekStart = startOfWeek;
      this.updateWeekDays();
    } else {
      console.log('goToToday: semana ya está actualizada');
    }
    
    // Seleccionar el día actual
    this.selectedDayIndex = todayDayOfWeek;
    this.selectedDay = this.weekDays[todayDayOfWeek];
    
    console.log('goToToday: día seleccionado:', this.selectedDayIndex, 'objeto día:', this.selectedDay);
    
    // Cambiar a vista semanal para que el botón "Hoy" funcione correctamente
    if (this.currentView !== 'week') {
      console.log('goToToday: cambiando a vista semanal');
      this.currentView = 'week';
    }
    
    console.log('goToToday: completado. Vista actual:', this.currentView, 'Día seleccionado:', this.selectedDayIndex);
    
    // Actualizar estados del calendario
    this.updateCalendarStates();
  }

  isTodaySelected(): boolean {
    return this._isTodaySelected;
  }

  // Verificar si el día seleccionado es hoy (para uso interno)
  private isSelectedDayToday(): boolean {
    if (!this.selectedDay) return false;
    
    const today = new Date();
    const selectedDate = this.selectedDay.fullDate;
    
    return today.toDateString() === selectedDate.toDateString();
  }

  // Verificar si el botón "Día" está activo
  isDayViewActive(): boolean {
    return this._isDayViewActive;
  }

  // Método privado para actualizar los estados del calendario (evita bucles)
  private updateCalendarStates(): void {
    // Actualizar estado del botón "Hoy"
    if (!this.selectedDay) {
      this._isTodaySelected = false;
    } else {
      const today = new Date();
      const selectedDate = this.selectedDay.fullDate;
      
      if (this.currentView === 'day') {
        this._isTodaySelected = false;
      } else {
        const todayDayOfWeek = today.getDay(); // 0 = domingo, 1 = lunes, etc.
        const isTodayInWeek = this.selectedDayIndex === todayDayOfWeek;
        const isDateToday = today.toDateString() === selectedDate.toDateString();
        this._isTodaySelected = isTodayInWeek && isDateToday;
      }
    }

    // Actualizar estado del botón "Día"
    this._isDayViewActive = this.currentView === 'day';
  }

  // Alternar sidebar móvil del calendario
  toggleMobileSidebar(): void {
    this.showMobileSidebar = !this.showMobileSidebar;
  }

  selectTimeSlot(day: any, hour: string) {
    console.log(`Slot seleccionado: ${day.name} ${day.date} a las ${hour}:00`);
    
    // Si hay una cita en este slot, mostrar modal con detalles
    const appointment = this.getAppointment(day, hour);
    if (appointment) {
      this.selectedAppointment = appointment;
      this.generateMeetingDetails();
      this.showAppointmentModal = true;
    } else {
      // Si no hay cita, mostrar opción para crear una nueva
      console.log('Slot disponible para programar cita');
    }
  }

  hasEvent(day: any, hour: string): boolean {
    // Verificar si hay eventos en el userAppointments para este día y hora
    if (!this.userAppointments || !day) return false;
    
    const dayNumber = parseInt(day.date.split('/')[0]);
    const hourNumber = parseInt(hour);
    
    return this.userAppointments.some(appointment => {
      // Convertir el horario de 12h a 24h para comparación correcta
      const appointmentHour24 = this.convertTo24Hour(appointment.time);
      const appointmentDate = new Date(appointment.date + 'T' + appointmentHour24 + ':00');
      const appointmentDay = appointmentDate.getDate();
      const appointmentHour = appointmentDate.getHours();
      
      return appointmentDay === dayNumber && appointmentHour === hourNumber;
    });
  }

  // Obtener la cita para un día y hora específicos
  getAppointment(day: any, hour: string): Appointment | undefined {
    if (!this.userAppointments || !day) return undefined;
    
    const dayNumber = parseInt(day.date.split('/')[0]);
    const hourNumber = parseInt(hour);
    
    return this.userAppointments.find(appointment => {
      // Convertir el horario de 12h a 24h para comparación correcta
      const appointmentHour24 = this.convertTo24Hour(appointment.time);
      const appointmentDate = new Date(appointment.date + 'T' + appointmentHour24 + ':00');
      const appointmentDay = appointmentDate.getDate();
      const appointmentHour = appointmentDate.getHours();
      
      return appointmentDay === dayNumber && appointmentHour === hourNumber;
    });
  }

  // Obtener el nombre del coach para mostrar en el tooltip
  getCoachName(day: any, hour: string): string {
    const appointment = this.getAppointment(day, hour);
    return appointment ? appointment.coach.name : '';
  }

  // Obtener el tipo de coach (Coach o Experto)
  getCoachType(day: any, hour: string): string {
    const appointment = this.getAppointment(day, hour);
    return appointment ? appointment.coach.type.name : '';
  }

  // Convertir horario de 12h (AM/PM) a 24h para comparación correcta
  convertTo24Hour(time12h: string): string {
    if (!time12h) return '00:00';
    
    // Si ya está en formato 24h, devolverlo tal como está
    if (!time12h.includes('AM') && !time12h.includes('PM')) {
      return time12h;
    }
    
    // Extraer hora, minutos y AM/PM
    const timeMatch = time12h.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) {
      console.warn('Formato de hora no reconocido:', time12h);
      return '00:00';
    }
    
    let hour = parseInt(timeMatch[1]);
    const minutes = timeMatch[2];
    const period = timeMatch[3].toUpperCase();
    
    // Convertir a formato 24h
    if (period === 'AM') {
      if (hour === 12) hour = 0; // 12:XX AM = 00:XX
    } else if (period === 'PM') {
      if (hour !== 12) hour += 12; // 1:XX PM = 13:XX, pero 12:XX PM = 12:XX
    }
    
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  }

  // Obtener las próximas citas ordenadas por fecha
  getUpcomingAppointments(): Appointment[] {
    if (!this.userAppointments || this.userAppointments.length === 0) {
      return [];
    }

    const now = new Date();
    const upcoming = this.userAppointments.filter(appointment => {
      // Convertir el horario de 12h a 24h para comparación correcta
      const appointmentHour24 = this.convertTo24Hour(appointment.time);
      const appointmentDate = new Date(appointment.date + 'T' + appointmentHour24 + ':00');
      return appointmentDate > now;
    });

    // Ordenar por fecha (más cercana primero)
    return upcoming.sort((a, b) => {
      const dateA = new Date(a.dateValue);
      const dateB = new Date(b.dateValue);
      return dateA.getTime() - dateB.getTime();
    }).slice(0, 3); // Mostrar solo las próximas 3 citas
  }

  // Calcular la próxima cita y determinar si mostrar el card
  calculateNextAppointment(): void {
    // Limpiar intervalo anterior si existe
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    const upcoming = this.getUpcomingAppointments();
    
    if (upcoming.length === 0) {
      this.nextAppointment = null;
      this.showAppointmentCard = false;
      this.daysUntilAppointment = 0;
      this.countdownTime = '';
      return;
    }

    this.nextAppointment = upcoming[0];
    const appointmentHour24 = this.convertTo24Hour(this.nextAppointment.time);
    const appointmentDateTime = new Date(this.nextAppointment.date + 'T' + appointmentHour24 + ':00');
    const now = new Date();

    // Calcular diferencia en milisegundos
    const diffMs = appointmentDateTime.getTime() - now.getTime();

    if (diffMs <= 0) {
      this.showAppointmentCard = false;
      return;
    }

    // Calcular días faltantes (solo fecha, sin hora)
    const appointmentDate = new Date(this.nextAppointment.date + 'T00:00:00');
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.floor((appointmentDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));

    // Mostrar card si faltan 3 días o menos (incluyendo el día de la cita)
    if (diffDays <= 3 && diffDays >= 0) {
      this.showAppointmentCard = true;
      this.daysUntilAppointment = diffDays;
      
      // Si es el mismo día, iniciar cuenta regresiva
      if (diffDays === 0) {
        this.updateCountdown();
        this.countdownInterval = setInterval(() => {
          this.updateCountdown();
        }, 1000);
      } else {
        this.countdownTime = '';
      }
    } else {
      this.showAppointmentCard = false;
    }
  }

  // Actualizar cuenta regresiva
  updateCountdown(): void {
    if (!this.nextAppointment) {
      return;
    }

    const appointmentHour24 = this.convertTo24Hour(this.nextAppointment.time);
    const appointmentDateTime = new Date(this.nextAppointment.date + 'T' + appointmentHour24 + ':00');
    const now = new Date();
    const diffMs = appointmentDateTime.getTime() - now.getTime();

    if (diffMs <= 0) {
      this.countdownTime = '';
      this.showAppointmentCard = false;
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      return;
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    this.countdownTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // Obtener texto para mostrar en el card
  getAppointmentCardText(): string {
    if (!this.nextAppointment) {
      return '';
    }

    if (this.daysUntilAppointment === 0) {
      if (this.countdownTime) {
        return `Tu siguiente Reunión es en ${this.countdownTime}`;
        //return `Reunión con tu mentor en ${this.countdownTime}`;
      } else {
        return 'Tu siguiente Reunión es hoy';
        //return 'Reunión con tu mentor hoy';
      }
    } else if (this.daysUntilAppointment === 1) {
      return 'Tu siguiente Reunión es mañana';
      //return 'Reunión con tu mentor mañana';
    } else {
      return `Tu siguiente Reunión es en ${this.daysUntilAppointment} días`;
      //return `Reunión con tu mentor en ${this.daysUntilAppointment} días`;
    }
  }

  // Navegar a la página de mentores para agendar cita
  goToMentors(): void {
    this.router.navigate(['/dashboard/mentors']);
  }

  // Verificar si debe mostrarse el banner de mentor (si tiene mentor asignado y no tiene citas)
  checkExecutiveEligibility() {
    console.log('checkExecutiveEligibility: ejecutándose...');
    console.log('user:', this.user);
    
    if (!this.user) {
      console.log('checkExecutiveEligibility: No hay usuario');
      return;
    }
    
    // Verificar si el usuario tiene un mentor asignado
    const hasMentorAssigned = this.user?.coachAssigned?.id;
    console.log('checkExecutiveEligibility: ¿Usuario tiene mentor asignado?', hasMentorAssigned);
    
    // Verificar si el usuario ya tuvo alguna cita con su mentor asignado
    const hasAppointmentWithMentor = this.hasAppointmentWithAssignedMentor();
    console.log('checkExecutiveEligibility: ¿Usuario ya tuvo cita con su mentor?', hasAppointmentWithMentor);
    
    // Mostrar el banner si:
    // 1. El usuario tiene mentor asignado
    // 2. El usuario NO ha tenido ninguna cita con su mentor asignado
    if (hasMentorAssigned && !hasAppointmentWithMentor) {
      console.log('checkExecutiveEligibility: Mostrando banner Executive - Usuario tiene mentor pero no tiene citas');
      this.showExecutiveModal = true;
      console.log('showExecutiveModal establecido en:', this.showExecutiveModal);
    } else {
      // Ocultar el banner si el usuario ya tiene citas con su mentor o no tiene mentor
      this.showExecutiveModal = false;
      if (!hasMentorAssigned) {
        console.log('checkExecutiveEligibility: Usuario no tiene mentor asignado');
      }
      if (hasAppointmentWithMentor) {
        console.log('checkExecutiveEligibility: Usuario ya tiene cita con su mentor, ocultando banner');
      }
    }
  }

  /**
   * Verifica si el usuario ya tuvo alguna cita con su mentor asignado
   * @returns true si el usuario tiene al menos una cita con su mentor asignado
   */
  hasAppointmentWithAssignedMentor(): boolean {
    // Si no hay usuario o no tiene mentor asignado, retornar false
    if (!this.user?.coachAssigned?.id) {
      console.log('hasAppointmentWithAssignedMentor: Usuario no tiene mentor asignado');
      return false;
    }

    const assignedMentorId = this.user.coachAssigned.id;
    console.log('hasAppointmentWithAssignedMentor: ID del mentor asignado:', assignedMentorId);

    // Si no hay citas cargadas aún, retornar false (no asumir que tiene citas)
    if (!this.userAppointments || this.userAppointments.length === 0) {
      console.log('hasAppointmentWithAssignedMentor: No hay citas cargadas');
      return false;
    }

    // Verificar si alguna cita tiene un coach.id que coincida con el mentor asignado
    const hasAppointment = this.userAppointments.some(appointment => {
      const coachId = appointment.coach?.id;
      const matches = coachId === assignedMentorId || coachId?.toString() === assignedMentorId?.toString();
      
      if (matches) {
        console.log('hasAppointmentWithAssignedMentor: Cita encontrada con mentor asignado:', appointment);
      }
      
      return matches;
    });

    console.log('hasAppointmentWithAssignedMentor: Resultado:', hasAppointment);
    return hasAppointment;
  }

  // Verificar si debe mostrarse el banner de Phoenix Sessions
  checkPhoenixSessionsBanner() {
    console.log('checkPhoenixSessionsBanner: ejecutándose...');
    console.log('user:', this.user);
    
    if (!this.user) {
      console.log('checkPhoenixSessionsBanner: No hay usuario');
      return;
    }
    
    // Verificar si estamos en la semana 4 o 6
    const currentWeek = this.getCurrentWeek();
    console.log('Semana actual:', currentWeek);
    
    if (currentWeek !== 4 && currentWeek !== 6) {
      console.log('checkPhoenixSessionsBanner: No es la semana 4 ni 6');
      this.showPhoenixSessionsBanner = false;
      return;
    }
    
    // Verificar si el usuario tiene el permiso correspondiente activo
    // Para semana 4: permiso id "3"
    // Para semana 6: permiso id "5"
    if (!this.user.permissions || !Array.isArray(this.user.permissions)) {
      console.log('checkPhoenixSessionsBanner: No hay permissions o no es un array');
      this.showPhoenixSessionsBanner = false;
      return;
    }
    
    // Buscar el permiso correspondiente según la semana
    const requiredPermissionId = currentWeek === 4 ? "3" : "5";
    const requiredPermission = this.user.permissions.find((p: any) => p.id === requiredPermissionId);
    
    console.log(`checkPhoenixSessionsBanner: Semana ${currentWeek}, buscando permiso id "${requiredPermissionId}"`);
    console.log('checkPhoenixSessionsBanner: Permiso encontrado:', requiredPermission);
    console.log('checkPhoenixSessionsBanner: Todos los permisos del usuario:', this.user.permissions);
    
    if (!requiredPermission) {
      console.log(`checkPhoenixSessionsBanner: No se encontró el permiso ${requiredPermissionId}`);
      this.showPhoenixSessionsBanner = false;
      return;
    }
    
    // Verificar que el permiso esté activo (para mostrar el banner)
    if (requiredPermission.active !== true) {
      console.log(`checkPhoenixSessionsBanner: Permiso ${requiredPermissionId} no está activo (active=${requiredPermission.active})`);
      this.showPhoenixSessionsBanner = false;
      return;
    }
    
    console.log(`checkPhoenixSessionsBanner: ✅ Permiso ${requiredPermissionId} está activo`);
    
    // Verificar si ya completó Phoenix Sessions para la semana correspondiente
    let phoenixSessionComplete = false;
    if (currentWeek === 4) {
      phoenixSessionComplete = this.user?.stepsAnswers?.step5?.phoenixSessions3Completed === true;
      console.log(`checkPhoenixSessionsBanner: Verificando phoenixSessions3Completed para semana 4:`, phoenixSessionComplete);
      console.log(`checkPhoenixSessionsBanner: stepsAnswers?.step5:`, this.user?.stepsAnswers?.step5);
    } else if (currentWeek === 6) {
      phoenixSessionComplete = this.user?.stepsAnswers?.step5?.phoenixSessions5Completed === true;
      console.log(`checkPhoenixSessionsBanner: Verificando phoenixSessions5Completed para semana 6:`, phoenixSessionComplete);
      console.log(`checkPhoenixSessionsBanner: stepsAnswers?.step5:`, this.user?.stepsAnswers?.step5);
    }

    // Verificar que no haya completado Phoenix Sessions para la semana actual
    if (phoenixSessionComplete === true) {
      console.log(`checkPhoenixSessionsBanner: ⚠️ Usuario ya completó Phoenix Sessions para la semana ${currentWeek}. NO mostrar banner.`);
      this.showPhoenixSessionsBanner = false;
      return;
    }
    
    // Si llegamos aquí, todas las condiciones se cumplen
    console.log('checkPhoenixSessionsBanner: ✅ TODAS LAS CONDICIONES CUMPLIDAS - Mostrando banner de Phoenix Sessions');
    console.log('checkPhoenixSessionsBanner: showPhoenixSessionsBanner = true');
    this.showPhoenixSessionsBanner = true;
  }

  // Confirmar asistencia a Phoenix Sessions
  confirmAttendance() {
    console.log('confirmAttendance: Usuario confirma asistencia a Phoenix Sessions');
    
    // Obtener la semana actual
    const currentWeek = this.getCurrentWeek();
    
    // Generar identificador único para Phoenix Sessions
    const phoenixSessionsId = 'phoenix-sessions-' + Date.now();
    
    // Redirigir a mi-mentor con parámetros para agendar 2 reuniones simultáneas
    this.router.navigate(['/dashboard/mi-mentor'], {
      queryParams: {
        phoenixSessions: true,
        sessionId: phoenixSessionsId,
        allowMultiple: true,
        maxAppointments: 2,
        source: 'phoenix-sessions-banner',
        week: currentWeek
      }
    });
  }

  /**
   * Verifica si debe mostrarse el banner de análisis profundo
   * El banner aparece durante los primeros 7 días desde user.createdAt
   * Calcula la fecha límite (createdAt + 7 días) y la muestra en formato dd/mm/aa
   * Después de 7 días, el banner se oculta automáticamente
   * Solo se muestra si step1Complete es false
   */
  checkDeepAnalysisBanner() {
    console.log('checkDeepAnalysisBanner: ejecutándose...');
    
    if (!this.user?.createdAt) {
      console.log('checkDeepAnalysisBanner: No hay createdAt del usuario');
      this.showDeepAnalysisBanner = false;
      return;
    }

    // Verificar si ya completó el step1 (step1Complete = true)
    const step1Complete = this.user?.stepsAnswers?.step1?.step1Complete === true;
    if (step1Complete) {
      console.log('checkDeepAnalysisBanner: Usuario ya completó step1, no se muestra el banner');
      this.showDeepAnalysisBanner = false;
      return;
    }

    const createdAt = new Date(this.user.createdAt);
    const now = new Date();
    
    // Calcular la fecha límite (createdAt + 7 días)
    const deadlineDate = new Date(createdAt);
    deadlineDate.setDate(deadlineDate.getDate() + 7);
    
    // Calcular la diferencia en milisegundos
    const diffInMs = now.getTime() - createdAt.getTime();
    
    // Convertir a días
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    console.log('checkDeepAnalysisBanner - diffInDays:', diffInDays);
    console.log('checkDeepAnalysisBanner - deadlineDate:', deadlineDate);
    
    // Mostrar el banner solo si han pasado menos de 7 días desde createdAt
    if (diffInDays < 7) {
      console.log('checkDeepAnalysisBanner: Mostrando banner de análisis profundo');
      this.showDeepAnalysisBanner = true;
      
      // Formatear la fecha límite en dd/mm/aa
      const day = String(deadlineDate.getDate()).padStart(2, '0');
      const month = String(deadlineDate.getMonth() + 1).padStart(2, '0');
      const year = String(deadlineDate.getFullYear()).slice(-2);
      this.deepAnalysisDeadlineDate = `${day}/${month}/${year}`;
      
      console.log('checkDeepAnalysisBanner - Fecha límite formateada:', this.deepAnalysisDeadlineDate);
    } else {
      console.log('checkDeepAnalysisBanner: Han pasado 7 o más días, no se muestra el banner (días transcurridos:', diffInDays, ')');
      this.showDeepAnalysisBanner = false;
    }
  }

  /**
   * Verifica si ya se completó el formulario de lista de espera en la semana 12
   * Si el usuario está en la semana 12, actualiza automáticamente el flag phoenixSessions7Completed = true
   */
  checkWaitlistFormCompleted() {
    console.log('checkWaitlistFormCompleted: ejecutándose...');
    
    if (!this.user) {
      console.log('checkWaitlistFormCompleted: No hay usuario');
      return;
    }

    const currentWeek = this.getCurrentWeek();
    const phoenixSessions7Completed = this.user?.stepsAnswers?.step5?.phoenixSessions7Completed === true;
    
    console.log('checkWaitlistFormCompleted - Semana actual:', currentWeek);
    console.log('checkWaitlistFormCompleted - phoenixSessions7Completed:', phoenixSessions7Completed);
    
    // Si estamos en la semana 12, actualizar el flag automáticamente
    if (currentWeek === 12) {
      if (phoenixSessions7Completed) {
        console.log('checkWaitlistFormCompleted: ✅ Usuario ya completó el formulario de lista de espera en semana 12');
      } else {
        console.log('checkWaitlistFormCompleted: ⚠️ Usuario está en semana 12, actualizando flag phoenixSessions7Completed...');
        
        // Actualizar el flag si estamos en semana 12 y aún no está completado
        const userId = this.user._id || this.user.id;
        if (userId) {
          this.usersService.updateStep5(
            userId,
            'phoenixSessions7Completed',
            new Date().toISOString()
          ).subscribe({
            next: (response) => {
              console.log('checkWaitlistFormCompleted: Flag phoenixSessions7Completed actualizado exitosamente');
              
              // Actualizar el usuario local con los datos actualizados
              if (response.data) {
                this.user = response.data;
                // También actualizar en el servicio de autenticación
                this.authService.updateUserData(response.data);
              }
            },
            error: (error) => {
              console.error('checkWaitlistFormCompleted: Error al actualizar phoenixSessions7Completed:', error);
            }
          });
        }
      }
    }
  }

  /**
   * Verifica si debe mostrarse el banner de graduación
   * El banner se muestra durante toda la semana 12
   * Solo se muestra si phoenixSessions8Completed es false
   */
  checkGraduationBanner() {
    console.log('checkGraduationBanner: ejecutándose...');
    
    // Validación inicial: verificar que el usuario existe
    if (!this.user) {
      console.log('checkGraduationBanner: No hay usuario');
      this.showGraduationBanner = false;
      return;
    }

    // Obtener la semana actual usando el método getCurrentWeek()
    const currentWeek = this.getCurrentWeek();
    console.log('checkGraduationBanner - Semana actual:', currentWeek);

    // Validación principal: verificar si ya completó la graduación (phoenixSessions8Completed = true)
    const phoenixSessions8Completed = this.user?.stepsAnswers?.step5?.phoenixSessions8Completed === true;
    console.log('checkGraduationBanner - phoenixSessions8Completed:', phoenixSessions8Completed);
    console.log('checkGraduationBanner - stepsAnswers?.step5:', this.user?.stepsAnswers?.step5);
    
    // Si ya completó la graduación, no mostrar el banner
    if (phoenixSessions8Completed) {
      console.log('checkGraduationBanner: Usuario ya completó phoenixSessions8Completed, no se muestra el banner');
      this.showGraduationBanner = false;
      return;
    }

    // Verificar si estamos en la semana 12 Y que phoenixSessions8Completed sea false
    if (currentWeek === 12 && !phoenixSessions8Completed) {
      console.log('checkGraduationBanner: ✅ Mostrando banner de graduación - Semana 12 y flag es false');
      this.showGraduationBanner = true;
    } else {
      console.log('checkGraduationBanner: No se muestra el banner', {
        currentWeek,
        isWeek12: currentWeek === 12,
        phoenixSessions8Completed
      });
      this.showGraduationBanner = false;
    }
  }

  /**
   * Verifica si debe mostrarse el banner de Tip Phoenix (Agendar con experto)
   * El banner se muestra a partir de la semana 7 en adelante
   */
  checkTipPhoenixBanner() {
    console.log('checkTipPhoenixBanner: ejecutándose...');
    
    // Validación inicial: verificar que el usuario existe
    if (!this.user) {
      console.log('checkTipPhoenixBanner: No hay usuario');
      this.showTipPhoenixBanner = false;
      return;
    }

    // Obtener la semana actual usando el método getCurrentWeek()
    const currentWeek = this.getCurrentWeek();
    console.log('checkTipPhoenixBanner - Semana actual:', currentWeek);

    // Verificar si estamos en la semana 7 o posterior
    if (currentWeek >= 7) {
      console.log('checkTipPhoenixBanner: ✅ Mostrando banner de Tip Phoenix - Semana 7 o posterior');
      this.showTipPhoenixBanner = true;
    } else {
      console.log('checkTipPhoenixBanner: No se muestra el banner - Semana anterior a la 7', {
        currentWeek,
        isWeek7OrLater: currentWeek >= 7
      });
      this.showTipPhoenixBanner = false;
    }
  }

  /**
   * Método para confirmar asistencia a la graduación
   * Actualiza el flag phoenixSessions8Completed en la base de datos
   * Envía correos de celebración al usuario executive y a su mentor asignado
   */
  confirmGraduation() {
    console.log('confirmGraduation: Usuario confirma asistencia a la graduación');
    
    if (!this.user?._id && !this.user?.id) {
      console.error('confirmGraduation: No hay ID de usuario disponible');
      return;
    }

    const userId = this.user._id || this.user.id;
    
    // Actualizar el flag phoenixSessions8Completed en la base de datos
    this.usersService.updateStep5(
      userId,
      'phoenixSessions8Completed',
      new Date().toISOString()
    ).subscribe({
      next: (response) => {
        console.log('confirmGraduation: Flag phoenixSessions8Completed actualizado exitosamente');
        
        // Actualizar el usuario local con los datos actualizados
        if (response.data) {
          this.user = response.data;
          // También actualizar en el servicio de autenticación
          this.authService.updateUserData(response.data);
        }
        
        // Enviar correos de celebración
        this.sendGraduationEmails();
        
        // Ocultar el banner después de confirmar
        this.showGraduationBanner = false;
      },
      error: (error) => {
        console.error('confirmGraduation: Error al actualizar phoenixSessions8Completed:', error);
        // Aún así ocultamos el banner para no bloquear la UI
        this.showGraduationBanner = false;
      }
    });
  }

  /**
   * Envía correos de celebración de graduación al usuario executive y a su mentor asignado
   */
  private sendGraduationEmails() {
    if (!this.user?.email) {
      console.error('sendGraduationEmails: No hay email del usuario disponible');
      return;
    }

    const userName = this.user.name || this.user.firstName || 'Usuario';
    const userEmail = this.user.email;

    // Enviar correo al usuario executive
    this.emailService.sendEmail({
      to: userEmail,
      subject: '¡Felicitaciones por tu Graduación Phoenix! 🎓',
      template: 'graduation-celebration',
      data: {
        clientName: userName,
        type: 'student'
      }
    }).subscribe({
      next: () => {
        console.log('sendGraduationEmails: Correo enviado exitosamente al usuario executive');
      },
      error: (error) => {
        console.error('sendGraduationEmails: Error al enviar correo al usuario executive:', error);
      }
    });

    // Enviar correo al mentor asignado si existe
    if (this.user.coachAssigned?.id) {
      // Obtener datos completos del mentor para obtener su email
      this.usersService.findById(this.user.coachAssigned.id).subscribe({
        next: (mentorResponse) => {
          if (mentorResponse?.data?.email) {
            const mentorName = mentorResponse.data.name || mentorResponse.data.firstName || 'Mentor';
            const mentorEmail = mentorResponse.data.email;

            this.emailService.sendEmail({
              to: mentorEmail,
              subject: 'Celebración: Tu estudiante se ha graduado en Phoenix 🎉',
              template: 'graduation-celebration',
              data: {
                mentorName: mentorName,
                clientName: userName,
                type: 'mentor'
              }
            }).subscribe({
              next: () => {
                console.log('sendGraduationEmails: Correo enviado exitosamente al mentor');
              },
              error: (error) => {
                console.error('sendGraduationEmails: Error al enviar correo al mentor:', error);
              }
            });
          } else {
            console.warn('sendGraduationEmails: El mentor no tiene email disponible');
          }
        },
        error: (error) => {
          console.error('sendGraduationEmails: Error al obtener datos del mentor:', error);
        }
      });
    } else {
      console.warn('sendGraduationEmails: El usuario no tiene mentor asignado');
    }
  }

  // Método de prueba para forzar la visualización del modal Executive
  testExecutiveModal() {
    console.log('testExecutiveModal: Forzando visualización del modal');
    this.showExecutiveModal = true;
    console.log('showExecutiveModal establecido en:', this.showExecutiveModal);
  }

  // Redirigir a Mi Mentor
  goToMiMentor() {
    // Navegar a Mi Mentor
    this.router.navigate(['/dashboard/mi-mentor']);
  }

  // Redirigir a Mi Mentor desde banner de salario
  goToMiMentorFromSalary() {
    // Cerrar el banner de salario
    this.closeSalaryBanner();
    
    // Navegar a Mi Mentor
    this.router.navigate(['/dashboard/mi-mentor']);
  }

  // Ver tareas pendientes
  viewPendingTasks() {
    // Navegar a la sección de objetivos/tareas
    this.router.navigate(['/dashboard/objectives']);
  }

  /**
   * Función genérica para verificar si estamos en X días antes de una semana específica
   * y si el usuario no tiene citas posteriores al inicio de esa semana
   * @param targetWeek - Número de semana objetivo (ej: 3)
   * @param daysBefore - Número de días antes de la semana (ej: 3)
   * @returns true si estamos en el rango Y no hay citas posteriores
   */
  private shouldShowMentorCard(targetWeek: number, daysBefore: number): boolean {
    if (!this.user?.createdAt) {
      return false;
    }

    const createdAt = new Date(this.user.createdAt);
    const now = new Date();

    // Normalizar ambas fechas al inicio del día en zona local
    const startOfCreatedAt = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calcular la diferencia en milisegundos
    const diffInMs = Math.max(0, startOfToday.getTime() - startOfCreatedAt.getTime());

    // Convertir a días (día 1 es el día de creación)
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;
    
    // Calcular el día de inicio de la semana objetivo (semana 1 = días 1-7, semana 2 = días 8-14, semana 3 = día 15)
    const weekStartDay = (targetWeek - 1) * 7 + 1;
    const startDay = weekStartDay - daysBefore;
    const endDay = weekStartDay - 1;
    
    console.log(`shouldShowMentorCard - Semana ${targetWeek}: día actual=${diffInDays}, rango=${startDay}-${endDay}, inicio semana=${weekStartDay}`);
    
    // Verificar si estamos en el rango de días antes de la semana objetivo
    if (!(diffInDays >= startDay && diffInDays <= endDay)) {
      console.log(`shouldShowMentorCard - No estamos en el rango de días antes de semana ${targetWeek}`);
      return false;
    }

    // Verificar si el usuario ya tiene alguna cita mayor o igual al día actual
    if (this.userAppointments && this.userAppointments.length > 0) {
      // Obtener la fecha actual (solo día, sin hora)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Inicio del día actual

      // Calcular la fecha de inicio de la semana objetivo (para logs)
      const weekStartDate = new Date(createdAt);
      weekStartDate.setDate(createdAt.getDate() + (weekStartDay - 1)); // -1 porque día 1 es createdAt
      weekStartDate.setHours(0, 0, 0, 0);

      console.log(`shouldShowMentorCard - Fecha de inicio semana ${targetWeek}:`, weekStartDate);
      console.log(`shouldShowMentorCard - Fecha actual (hoy):`, today);
      console.log(`shouldShowMentorCard - Total de citas del usuario:`, this.userAppointments.length);

      // Verificar si hay alguna cita con fecha >= día actual
      const futureAppointments: Array<{ date: Date, appointment: Appointment }> = [];
      
      this.userAppointments.forEach((appointment, index) => {
        let appointmentDate: Date | null = null;
        
        console.log(`shouldShowMentorCard - Analizando cita ${index + 1}/${this.userAppointments.length}:`, {
          date: appointment.date,
          dateValue: appointment.dateValue,
          coach: appointment.coach?.name
        });
        
        // Parsear la fecha de la cita
        if (appointment.dateValue) {
          appointmentDate = new Date(appointment.dateValue);
          console.log(`shouldShowMentorCard - Cita ${index + 1} - Parseada desde dateValue:`, appointmentDate);
        } else if (appointment.date) {
          // Si la fecha viene en formato string YYYY-MM-DD
          if (appointment.date.includes('T')) {
            appointmentDate = new Date(appointment.date);
            console.log(`shouldShowMentorCard - Cita ${index + 1} - Parseada desde date (con T):`, appointmentDate);
          } else {
            appointmentDate = new Date(appointment.date + 'T00:00:00');
            console.log(`shouldShowMentorCard - Cita ${index + 1} - Parseada desde date (sin T):`, appointmentDate);
          }
        } else {
          console.log(`shouldShowMentorCard - Cita ${index + 1} - No se pudo parsear la fecha (sin date ni dateValue)`);
          return;
        }
        
        if (!appointmentDate || isNaN(appointmentDate.getTime())) {
          console.log(`shouldShowMentorCard - Cita ${index + 1} - Fecha inválida:`, appointmentDate);
          return;
        }
        
        appointmentDate.setHours(0, 0, 0, 0); // Inicio del día para comparar
        
        console.log(`shouldShowMentorCard - Cita ${index + 1} - Comparando:`, {
          fechaCita: appointmentDate,
          fechaActual: today,
          esPosteriorOIgual: appointmentDate >= today,
          diferenciaDias: Math.round((appointmentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        });
        
        // Verificar si la cita es posterior o igual al día actual
        if (appointmentDate >= today) {
          futureAppointments.push({ date: appointmentDate, appointment });
          console.log(`shouldShowMentorCard - ✅ Cita ${index + 1} ENCONTRADA como >= día actual:`, {
            fecha: appointmentDate,
            fechaActual: today,
            coach: appointment.coach?.name,
            date: appointment.date,
            dateValue: appointment.dateValue
          });
        } else {
          console.log(`shouldShowMentorCard - Cita ${index + 1} es anterior al día actual, no cuenta`);
        }
      });

      // Si tiene citas >= día actual, no mostrar el card
      if (futureAppointments.length > 0) {
        console.log(`shouldShowMentorCard - ✅ CONDICIÓN CUMPLIDA: Usuario ya tiene ${futureAppointments.length} cita(s) >= día actual. NO mostrar card de mentor.`);
        console.log(`shouldShowMentorCard - Detalle de citas >= día actual:`, futureAppointments.map(f => ({
          fecha: f.date,
          coach: f.appointment.coach?.name
        })));
        return false;
      } else {
        console.log(`shouldShowMentorCard - No se encontraron citas >= día actual`);
      }
    } else {
      console.log(`shouldShowMentorCard - El usuario no tiene citas registradas`);
    }

    console.log(`shouldShowMentorCard - ✅ CONDICIÓN CUMPLIDA: Condiciones cumplidas para semana ${targetWeek}. Mostrar card de mentor.`);
    return true;
  }

  /**
   * Getter para verificar si debemos mostrar el card de mentor
   * Verifica tanto para la semana 3 como para la semana 6
   * Usa caché para evitar recálculos repetitivos en el template
   */
  get isThreeDaysBeforeWeek3(): boolean {
    if (this._shouldShowMentorCard === null) {
      // Verificar si debe mostrarse para semana 3 O semana 6
      const shouldShowWeek3 = this.shouldShowMentorCard(3, 3);
      const shouldShowWeek6 = this.shouldShowMentorCard(6, 3);
      const shouldShowWeek7 = this.shouldShowMentorCard(10, 3);
      this._shouldShowMentorCard = shouldShowWeek3 || shouldShowWeek6 || shouldShowWeek7;
    }
    return this._shouldShowMentorCard;
  }
  
  /**
   * Limpia la caché del card de mentor (útil cuando cambian las citas o el usuario)
   */
  private clearMentorCardCache(): void {
    this._shouldShowMentorCard = null;
  }

  // Redirigir a Mi Mentor desde el card de mentor
  goToMiMentorFromCard() {
    this.router.navigate(['/dashboard/mi-mentor']);
  }

  // Navegar a Academy
  goToAcademy() {
    this.router.navigate(['/dashboard/academy']);
  }

  /**
   * Verifica si el usuario tiene objetivos y actividades pendientes
   * @returns true si hay objetivos con al menos una actividad pendiente (estado diferente a "Completado")
   */
  get hasPendingObjectivesOrActivities(): boolean {
    if (!this.objetivos || this.objetivos.length === 0) {
      return false;
    }

    // Verificar si hay algún objetivo con actividades pendientes
    return this.objetivos.some(objetivo => {
      // Si el objetivo tiene actividades
      if (objetivo.activities && objetivo.activities.length > 0) {
        // Verificar si hay al menos una actividad pendiente (no completada)
        return objetivo.activities.some(actividad => actividad.estado !== 'Completado');
      }
      // Si no tiene actividades, considerar el objetivo como pendiente si no está completado
      // (esto depende de cómo se maneje, por ahora solo verificamos actividades)
      return false;
    });
  }

  /**
   * Verifica si hay algún tasks-card visible
   * @returns true si hay al menos un tasks-card visible
   */
  get hasVisibleTasksCard(): boolean {
    return this.isThreeDaysBeforeWeek3 || 
           this.showAppointmentCard || 
           this.hasPendingObjectivesOrActivities;
  }

  // Obtener nombre del usuario para el modal
  getUserFirstName(): string {
    return this.user?.firstName || this.user?.name || 'Usuario';
  }

  // Obtener URL de la imagen del modal Executive
  getExecutiveModalImageUrl(): string {
    const baseUrl = environment.apiUrlClean;
    return `${baseUrl}/executive-bg.jpg`;
  }

  // Obtener URL del video de Phoenix Professional
  getProfessionalVideoUrl(): string {
    return environment.urlVideoProfessional;
  }

  // Obtener ID del video de Phoenix Professional para el reproductor de YouTube
  getProfessionalVideoId(): string {
    return YouTubePlayerComponent.extractVideoId(this.getProfessionalVideoUrl()) || '';
  }

  // Obtener parámetros del video para el reproductor de YouTube
  getVideoParams(): string {
    return 'autoplay=0&mute=0&enablejsapi=1&origin=' + window.location.origin + '&rel=0&showinfo=0';
  }

  // Cerrar banner de felicitaciones
  closeSalaryBanner() {
    this.showSalaryBanner = false;
  }

  // Agendar entrevista desde banner
  scheduleInterviewFromBanner() {
    const userName = this.user?.firstName || this.user?.name || 'Usuario';
    const message = environment.whatsapp.executiveMessage.replace('{userName}', userName);
    const phoneNumber = environment.whatsapp.phoneNumber;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    
    // Abrir WhatsApp en nueva ventana
    window.open(whatsappUrl, '_blank');
  }

  // Abrir modal de lista de espera
  openWaitlistModal() {
    // Verificar si estamos en la semana 12 y actualizar el flag si es necesario
    const currentWeek = this.getCurrentWeek();
    if (currentWeek === 12 && this.user) {
      const userId = this.user._id || this.user.id;
      const phoenixSessions7Completed = this.user?.stepsAnswers?.step5?.phoenixSessions7Completed;
      
      // Solo actualizar si aún no se ha completado
      if (!phoenixSessions7Completed && userId) {
        this.usersService.updateStep5(
          userId,
          'phoenixSessions7Completed',
          new Date().toISOString()
        ).subscribe({
          next: (response) => {
            console.log('openWaitlistModal: Flag phoenixSessions7Completed actualizado exitosamente');
            
            // Actualizar el usuario local con los datos actualizados
            if (response.data) {
              this.user = response.data;
              // También actualizar en el servicio de autenticación
              this.authService.updateUserData(response.data);
            }
          },
          error: (error) => {
            console.error('openWaitlistModal: Error al actualizar phoenixSessions7Completed:', error);
          }
        });
      }
    }
    
    // Pre-llenar algunos campos con información del usuario si está disponible
    if (this.user) {
      this.waitlistForm.nombre = this.user.firstName || '';
      this.waitlistForm.apellido = this.user.lastName || '';
      this.waitlistForm.correoElectronico = this.user.email || '';
      this.waitlistForm.telefono = this.user.phone || '';
    }
    this.showWaitlistModal = true;
  }

  // Cerrar modal de lista de espera
  closeWaitlistModal() {
    this.showWaitlistModal = false;
    this.resetWaitlistForm();
  }

  // Resetear formulario de lista de espera
  resetWaitlistForm() {
    this.waitlistForm = {
      nombre: '',
      apellido: '',
      telefono: '',
      correoElectronico: '',
      provincia: '',
      distrito: ''
    };
  }

  // Obtener distritos por provincia
  getDistritosByProvincia(): string[] {
    if (this.waitlistForm.provincia && this.distritos[this.waitlistForm.provincia]) {
      return this.distritos[this.waitlistForm.provincia];
    }
    return [];
  }

  // Limpiar distrito cuando cambia la provincia
  onProvinciaChange() {
    this.waitlistForm.distrito = '';
  }

  // Validar formulario de lista de espera
  isWaitlistFormValid(): boolean {
    return !!(
      this.waitlistForm.nombre?.trim() &&
      this.waitlistForm.apellido?.trim() &&
      this.waitlistForm.telefono?.trim() &&
      this.waitlistForm.correoElectronico?.trim() &&
      this.waitlistForm.provincia?.trim() &&
      this.waitlistForm.distrito?.trim()
    );
  }

  // Método para mostrar alertas
  alert(message: string, title: string = ""): void {
    Swal.fire({
      title: title,
      html: message,
      confirmButtonText: 'Aceptar',
      customClass: {
        container: 'alert-container',
        confirmButton: 'alert-confirmButton'
      }
    });
  }

  // Enviar formulario de lista de espera
  async submitWaitlistForm() {
    if (!this.isWaitlistFormValid()) {
      this.alert('Por favor, completa todos los campos requeridos.');
      return;
    }

    this.isSubmittingWaitlist = true;
    this.loader.show();

    try {
      // Preparar datos para enviar
      const waitlistData = {
        ...this.waitlistForm,
        userId: this.authUser?.id || undefined,
        createdAt: new Date().toISOString()
      };

      // Llamar al servicio de waitlist
      await this.waitlistService.registerToWaitlist(waitlistData).toPromise();
      
      this.closeWaitlistModal();
      this.alert('¡Gracias! Te has registrado exitosamente en nuestra lista de espera. Te contactaremos pronto.');
    } catch (error) {
      console.error('Error al registrar en lista de espera:', error);
      this.alert('Hubo un error al registrar tu solicitud. Por favor, intenta nuevamente.');
    } finally {
      this.isSubmittingWaitlist = false;
      this.loader.hide();
    }
  }

  // ===== FUNCIONES DEL CALENDARIO MENSUAL =====
  
  // Inicializar el calendario mensual
  initializeMiniCalendar() {
    this.updateMiniCalendar();
  }

  // Actualizar el calendario mensual
  updateMiniCalendar() {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    
    // Obtener el primer día del mes
    const firstDay = new Date(year, month, 1);
    // Obtener el último día del mes
    const lastDay = new Date(year, month + 1, 0);
    
    // Obtener el día de la semana del primer día (0 = domingo, 1 = lunes, etc.)
    const firstDayOfWeek = firstDay.getDay();
    // Obtener el número total de días en el mes
    const totalDays = lastDay.getDate();
    
    // Generar el array de días del calendario
    this.miniCalendarDays = [];
    
    // Agregar días del mes anterior para completar la primera semana
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      this.miniCalendarDays.push({
        day: prevMonthDays - i,
        isOtherMonth: true,
        isToday: false,
        hasEvent: false,
        date: new Date(year, month - 1, prevMonthDays - i)
      });
    }
    
    // Agregar días del mes actual
    const today = new Date();
    for (let i = 1; i <= totalDays; i++) {
      const currentDate = new Date(year, month, i);
      const isToday = today.toDateString() === currentDate.toDateString();
      const hasEvent = this.hasEventOnDate(currentDate);
      
      this.miniCalendarDays.push({
        day: i,
        isOtherMonth: false,
        isToday: isToday,
        hasEvent: hasEvent,
        date: currentDate
      });
    }
    
    // Agregar días del mes siguiente para completar la última semana
    const remainingSlots = 42 - this.miniCalendarDays.length; // 6 semanas * 7 días = 42
    for (let i = 1; i <= remainingSlots; i++) {
      this.miniCalendarDays.push({
        day: i,
        isOtherMonth: true,
        isToday: false,
        hasEvent: false,
        date: new Date(year, month + 1, i)
      });
    }
    
    // Actualizar el texto del mes y año
    this.updateMonthYearText();
  }

  // Verificar si hay eventos en una fecha específica
  hasEventOnDate(date: Date): boolean {
    if (!this.userAppointments || this.userAppointments.length === 0) {
      return false;
    }
    
    const targetDate = date.toDateString();
    return this.userAppointments.some(appointment => {
      const appointmentDate = new Date(appointment.date + 'T00:00:00');
      return appointmentDate.toDateString() === targetDate;
    });
  }

  // Actualizar el texto del mes y año
  updateMonthYearText() {
    const months = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const month = months[this.currentMonth.getMonth()];
    const year = this.currentMonth.getFullYear();
    this.currentMonthYear = `${month}. ${year}`;
  }

  // Navegar al mes anterior
  navigateMonth(direction: number) {
    this.currentMonth.setMonth(this.currentMonth.getMonth() + direction);
    this.updateMiniCalendar();
  }

  // Seleccionar un día del calendario mensual
  selectMiniCalendarDay(dayData: any) {
    // Evitar bucles infinitos
    if (this._isNavigatingMonth) {
      return;
    }
    
    if (dayData.isOtherMonth) {
      // Si es de otro mes, navegar a ese mes
      const targetDate = dayData.date;
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();
      const currentMonth = this.currentMonth.getMonth();
      const currentYear = this.currentMonth.getFullYear();
      
      // Calcular la diferencia de meses
      const monthDiff = (targetYear - currentYear) * 12 + (targetMonth - currentMonth);
      
      if (monthDiff !== 0) {
        this._isNavigatingMonth = true;
        
        // Navegar al mes correcto
        this.currentMonth.setMonth(this.currentMonth.getMonth() + monthDiff);
        this.updateMiniCalendar();
        
        // Seleccionar el día después de navegar
        setTimeout(() => {
          this._isNavigatingMonth = false;
          
          // Buscar el día correspondiente en el nuevo calendario
          const dayNumber = targetDate.getDate();
          const newDayData = this.miniCalendarDays.find(day => 
            day.day === dayNumber && 
            !day.isOtherMonth &&
            day.date.getDate() === dayNumber
          );
          
          if (newDayData) {
            this.selectMiniCalendarDay(newDayData);
          }
        }, 100);
      }
      return;
    }
    
    // Detectar si estamos en tablet (820x1180)
    const isTablet = window.innerWidth >= 769 && window.innerWidth <= 1024;
    
    const selectedDate = dayData.date;
    
    if (isTablet) {
      // En tablet, solo filtrar las citas sin cambiar la vista ni ocultar el calendario
      this.updateEventsByDay(selectedDate);
      // Mantener la vista mensual
      this.currentView = 'month';
    } else {
      // En desktop/móvil, comportamiento original
      const dayOfWeek = selectedDate.getDay(); // 0 = domingo, 1 = lunes, etc.
      
      // Calcular el inicio de la semana que contiene el día seleccionado
      const startOfWeek = new Date(selectedDate);
      startOfWeek.setDate(selectedDate.getDate() - dayOfWeek);
      
      // Actualizar la semana actual del calendario principal
      this.currentWeekStart = startOfWeek;
      this.updateWeekDays();
      
      // Cambiar a vista semanal y seleccionar el día
      this.currentView = 'week';
      this.selectedDayIndex = dayOfWeek;
      this.selectedDay = this.weekDays[dayOfWeek];
      
      // Actualizar la lista de eventos con la fecha seleccionada
      this.updateEventsByDay(selectedDate);
      
      // Hacer scroll al calendario principal
      this.scrollToCalendar();
    }
  }

  // Hacer scroll al calendario principal
  scrollToCalendar() {
    const calendarElement = document.querySelector('.calendario-card');
    if (calendarElement) {
      calendarElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // Cambiar a vista de mes
  changeToMonthView() {
    this.currentView = 'month';
    this.updateMiniCalendar();
  }

  // Verificar si un día está seleccionado
  isSelectedDay(dayData: any): boolean {
    if (!dayData || dayData.isOtherMonth) return false;
    
    if (this.currentView === 'week' && this.selectedDay) {
      return this.selectedDay.fullDate.toDateString() === dayData.date.toDateString();
    }
    
    return false;
  }

  // ===== FUNCIONES DEL MODAL DE CITAS =====
  
  // Generar detalles de la reunión una sola vez
  private generateMeetingDetails(): void {
    console.log('selectedAppointment', this.selectedAppointment);
    // Usar el ID del appointment como meetingId
    this.meetingId = this.selectedAppointment?._id || '';
    //this.accessCode = Math.floor(Math.random() * 900000) + 100000 + '';
    // Usar la URL del environment
    this.meetingLink = `${environment.meetingUrl}${this.meetingId}`;
  }
  
  // Cerrar modal de detalles de cita
  closeAppointmentModal(): void {
    this.showAppointmentModal = false;
    this.selectedAppointment = null;
    // Limpiar los valores generados
    this.meetingId = '';
    this.accessCode = '';
    this.meetingLink = '';
  }

  // Obtener ID de reunión generado
  getMeetingId(): string {
    return this.meetingId;
  }

  // Obtener código de acceso generado
  getAccessCode(): string {
    return this.accessCode;
  }

  // Obtener enlace de reunión generado
  getMeetingLink(): string {
    return `${environment.meetingUrl}${this.meetingId.substring(0, 5)}...`;
  }

  // Unirse a la reunión
  joinMeeting(): void {
    window.open(this.meetingLink, '_blank');
  }

  // Copiar enlace de la reunión
  copyMeetingLink(): void {
    navigator.clipboard.writeText(this.meetingLink).then(() => {
      // Mostrar notificación de éxito
      console.log('Enlace copiado al portapapeles');
      // Aquí podrías mostrar un toast o notificación
    }).catch(err => {
      console.error('Error al copiar enlace:', err);
    });
  }

  // Enviar información de la cita por email
  sendAppointmentInfoByEmail(): void {
    if (!this.selectedAppointment || !this.user) {
      console.error('No hay cita seleccionada o usuario disponible');
      return;
    }

    // Mostrar loading
    this.loader.show();

    // Preparar datos del email
    const emailData: EmailData = {
      to: this.user.email,
      subject: 'Información de tu cita con Phoenix - Detalles de la reunión',
      template: 'appointment-info',
      data: {
        clientName: this.user.firstName || this.user.name || 'Cliente',
        mentorName: this.selectedAppointment.coach.name,
        sessionDate: this.selectedAppointment.date.toString(),
        sessionTime: this.selectedAppointment.time.toString(),
        meetingId: this.meetingId,
        accessCode: this.getAccessCode(),
        meetingLink: this.meetingLink,
        coachType: this.selectedAppointment.coach.type.name
      }
    };

    // Enviar el email
    this.emailService.sendAppointmentConfirmation(emailData).subscribe({
      next: (response) => {
        this.loader.hide();
        console.log('Email enviado exitosamente:', response);
        
        // Mostrar mensaje de éxito
        Swal.fire({
          title: '¡Email enviado!',
          text: 'La información de tu cita ha sido enviada a tu correo electrónico.',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          customClass: {
            container: 'alert-container',
            confirmButton: 'alert-confirmButton'
          }
        });
      },
      error: (error) => {
        this.loader.hide();
        console.error('Error al enviar email:', error);
        
        // Mostrar mensaje de error
        Swal.fire({
          title: 'Error al enviar email',
          text: 'No se pudo enviar la información por correo. Por favor, intenta nuevamente.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          customClass: {
            container: 'alert-container',
            confirmButton: 'alert-confirmButton'
          }
        });
      }
    });
  }

  // Formatear fecha de la cita
  private formatAppointmentDate(dateValue: string): string {
    try {
      const date = new Date(dateValue);
      return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Fecha no disponible';
    }
  }

  // Formatear hora de la cita
  private formatAppointmentTime(dateValue: string): string {
    try {
      const date = new Date(dateValue);
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Lima'
      });
    } catch (error) {
      console.error('Error al formatear hora:', error);
      return 'Hora no disponible';
    }
  }

  // Métodos para el componente table
  onObjetivoDeleted(id: string): void {
    // Recargar la lista de objetivos después de eliminar uno
    this.reloadObjectivesFromService();
  }

  onDesignResponsive(isResponsive: boolean): void {
    // Manejar cambios de diseño responsive si es necesario
    console.log('Diseño responsive:', isResponsive);
  }

  onStateChanged(): void {
    // Recargar objetivos cuando cambie el estado
    this.reloadObjectivesFromService();
  }

  reloadObjectivesList(): void {
    // Recargar la lista completa de objetivos
    this.reloadObjectivesFromService();
  }

  private reloadObjectivesFromService(): void {
    // Recargar objetivos desde el servicio
    this.objectivesService.findAll(this.authUser.id).subscribe({
      next: (response) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.objetivosOriginales = response.data;
          this.procesarObjetivos();
        }
      },
      error: (error) => {
        console.error("Error al recargar objetivos:", error);
      }
    });
  }

  // ===== FUNCIONES DEL BOTÓN FLOTANTE Y MODAL DE VIDEO =====
  
  // Verificar si debe reproducir el video automáticamente
  private checkAutoPlayVideo(): void {
    // Verificar que el usuario esté disponible antes de continuar
    if (!this.user) {
      console.warn('Usuario no disponible en checkAutoPlayVideo');
      return;
    }

    // Verificar si viene del flujo de completar perfil
    const fromProfileComplete = sessionStorage.getItem('fromProfileComplete');
    console.log('fromProfileComplete', fromProfileComplete);
    console.log('user?.isCompleteInfo', this.user?.isCompleteInfo);
    
    if (fromProfileComplete === 'true' && this.user?.isCompleteInfo) {
      this.shouldAutoPlayVideo = true;
      // Limpiar la bandera
      sessionStorage.removeItem('fromProfileComplete');
      
      // Esperar un poco para que el DOM esté listo y luego abrir el modal
      setTimeout(() => {
        this.onFloatingButtonClick();
      }, 1000);
    }
  }

  // Click en el botón flotante
  onFloatingButtonClick(): void {
    this.showVideoModal = true;
    console.log('Modal de video abierto desde dashboard-menu');
  }

  // Cerrar modal de video
  closeVideoModal(): void {
    this.showVideoModal = false;
    console.log('Modal de video cerrado');
  }

  // Obtener tooltip del botón flotante
  getFloatingButtonTooltip(): string {
    return 'Ver video de bienvenida';
  }

  // Método para ver objetivos
  viewObjectives(): void {
    this.router.navigate(['/dashboard/objectives']);
  }

  // Método para agregar objetivo
  addObjective(): void {
    this.router.navigate(['/dashboard/new-objective'], {
      state: { objetivo: null, editable: true }
    });
  }

  // Método para actualizar el step actual dinámicamente
  updateCurrentStep(step: number): void {
    if (step >= 1 && step <= 8) {
      this.currentStep = step;
    }
  }

  // Método para obtener el progreso del step actual
  getCurrentStepProgress(): number {
    return (this.currentStep / 8) * 100;
  }

  // Método para actualizar el estado de un step específico
  updateStepStatus(stepNumber: number, status: 'completed' | 'in-progress' | 'pending', statusText?: string): void {
    const step = this.progressSteps.find(s => s.number === stepNumber);
    if (step) {
      step.status = status;
      if (statusText) {
        step.statusText = statusText;
      }
    }
  }

  // Método para obtener el estado de un step específico
  getStepStatus(stepNumber: number): string {
    const step = this.progressSteps.find(s => s.number === stepNumber);
    return step ? step.status : 'pending';
  }

  // Método para habilitar/deshabilitar un step
  toggleStepEnabled(stepNumber: number, enabled: boolean): void {
    const step = this.progressSteps.find(s => s.number === stepNumber);
    if (step) {
      step.enabled = enabled;
    }
  }

  // Método para actualizar todos los steps basado en el progreso del usuario
  updateStepsBasedOnProgress(): void {
    // NOTA: Esta función ya NO es necesaria porque los estados de los steps
    // se calculan dinámicamente desde CronWeek en mapCronWeekToSteps()
    // Dejamos esta función vacía para mantener compatibilidad, pero ya no modifica los estados
    
    // La lógica antigua que sobrescribía los estados ha sido removida
    // Ahora todo se basa en el estado de las semanas en CronWeek
    
    console.log('✅ updateStepsBasedOnProgress: Estados se calculan desde CronWeek, no desde aquí');
  }

  // Métodos para la lista de eventos
  updateEventsByDay(selectedDate?: Date): void {
    if (!this.userAppointments || this.userAppointments.length === 0) {
      this.eventsByDay = [];
      return;
    }

    // Agrupar eventos por día
    const eventsByDay: { [key: string]: any } = {};
    
    // Crear array de todas las citas con sus fechas para ordenar
    const appointmentsWithDates: Array<{ appointment: Appointment, date: Date }> = [];
    
    this.userAppointments.forEach(appointment => {
      // Crear fecha local sin problemas de zona horaria
      let appointmentDate: Date;
      if (appointment.date.includes('-')) {
        // Formato YYYY-MM-DD
        const dateParts = appointment.date.split('-');
        appointmentDate = new Date(
          parseInt(dateParts[0]), 
          parseInt(dateParts[1]) - 1, 
          parseInt(dateParts[2])
        );
      } else {
        // Otros formatos, usar Date normal
        appointmentDate = new Date(appointment.date);
      }
      
      appointmentsWithDates.push({ appointment, date: appointmentDate });
    });

    // Si se proporciona una fecha seleccionada, filtrar solo los eventos de ese día
    let filteredAppointments = appointmentsWithDates;
    if (selectedDate) {
      const selectedDateStr = selectedDate.toDateString();
      filteredAppointments = appointmentsWithDates.filter(item => {
        return item.date.toDateString() === selectedDateStr;
      });
    } else {
      // Por defecto: ordenar por fecha descendente (más recientes primero) y tomar las 5 últimas
      filteredAppointments = appointmentsWithDates
        .sort((a, b) => b.date.getTime() - a.date.getTime()) // Orden descendente
        .slice(0, 5); // Tomar solo las 5 últimas
    }
    
    // Agrupar los eventos filtrados por día
    filteredAppointments.forEach(({ appointment, date: appointmentDate }) => {
      const dayKey = appointmentDate.toDateString();
      
      if (!eventsByDay[dayKey]) {
        eventsByDay[dayKey] = {
          dayName: this.getEventDayName(appointmentDate),
          date: this.formatEventDate(appointmentDate),
          dateObj: appointmentDate, // Guardar la fecha para ordenar
          events: []
        };
      }
      
      eventsByDay[dayKey].events.push({
        time: appointment.time,
        type: appointment.coach.type.name,
        title: appointment.coach.name
      });
    });

    // Convertir a array y ordenar por fecha descendente (más recientes primero)
    this.eventsByDay = Object.values(eventsByDay).sort((a: any, b: any) => {
      // Ordenar usando el dateObj guardado en orden descendente
      return b.dateObj.getTime() - a.dateObj.getTime();
    });
  }

  getEventDayName(date: Date): string {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[date.getDay()];
  }

  formatEventDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} de ${month}, ${year}`;
  }


  toggleDayMenu(date: string): void {
    // Implementar lógica para mostrar menú del día
    console.log('Toggle menu for date:', date);
  }

  getEventLineClass(eventType: string): string {
    // Asignar colores según el tipo de evento
    if (eventType.toLowerCase().includes('reunión') || eventType.toLowerCase().includes('fabrica') || eventType.toLowerCase().includes('interna')) {
      return 'line-green';
    } else if (eventType.toLowerCase().includes('actividad') || eventType.toLowerCase().includes('integracion')) {
      return 'line-blue';
    }
    return 'line-default';
  }

  /**
   * Mapea las semanas de CronWeek del backend a los progressSteps del frontend
   * Actualiza dinámicamente el estado de cada step según el estado de sus semanas
   */
  mapCronWeekToSteps(): void {
    // Verificar si el usuario tiene CronWeek
    let cronWeek = this.user?.type?.CronWeek;
    
    if (!cronWeek) {
      console.log('⚠️ No hay CronWeek disponible para mapear');
      return;
    }

    console.log('📊 Mapeando CronWeek a progressSteps...');
    console.log('📊 CronWeek recibido (tipo):', typeof cronWeek);
    console.log('📊 CronWeek recibido (raw):', cronWeek);

    // Si CronWeek es un string, parsearlo
    if (typeof cronWeek === 'string') {
      try {
        console.log('📊 CronWeek es un string, parseando...');
        // Primero parsear el array de strings
        const parsedArray = JSON.parse(cronWeek);
        console.log('📊 Array parseado:', parsedArray);
        
        // Luego parsear cada elemento del array
        cronWeek = parsedArray.map((item: string) => JSON.parse(item));
        console.log('📊 CronWeek parseado completamente:', cronWeek);
      } catch (error) {
        console.error('❌ Error al parsear CronWeek:', error);
        return;
      }
    }

    if (!Array.isArray(cronWeek) || cronWeek.length === 0) {
      console.log('⚠️ CronWeek no es un array válido o está vacío');
      return;
    }

    // Mapear cada step con sus semanas correspondientes
    this.stepWeeksConfig.forEach((config) => {
      const step = this.progressSteps.find(s => s.number === config.step);
      
      if (!step) return;

      // Obtener las semanas asignadas a este step
      const assignedWeeks = cronWeek.filter((week: any) => {
        const weekNumber = parseInt(week.name.replace('semana', ''));
        return weekNumber >= config.weekStart && weekNumber <= config.weekEnd;
      });

      // Asignar las semanas al step
      step.weeks = assignedWeeks;

      // Calcular el estado del step basado en el estado de sus semanas
      if (assignedWeeks.length > 0) {
        // Contar estados de las semanas
        const allCompleted = assignedWeeks.every((w: any) => w.status === 'completed');
        const hasInProgress = assignedWeeks.some((w: any) => w.status === 'in-progress');
        const hasCompleted = assignedWeeks.some((w: any) => w.status === 'completed');
        const allPendingOrEmpty = assignedWeeks.every((w: any) => !w.status || w.status === 'pending' || w.status === '');

        // Determinar el estado del step según la lógica especificada
        if (allCompleted) {
          // Solo si TODAS las semanas están completadas
          step.status = 'completed';
          step.statusText = `Culminado - Sem ${this.getWeekRange(config.weekStart, config.weekEnd)}`;
        } else if (hasInProgress || hasCompleted) {
          // Si al menos una semana está en progreso o completada (pero no todas completadas)
          step.status = 'in-progress';
          step.statusText = `En curso - Sem ${this.getWeekRange(config.weekStart, config.weekEnd)}`;
        } else {
          // Si todas están pendientes o vacías
          step.status = 'pending';
          step.statusText = `Pendiente - Sem ${this.getWeekRange(config.weekStart, config.weekEnd)}`;
        }

        console.log(`📊 Step ${config.step} - Semanas: ${assignedWeeks.map((w: any) => w.name + ':' + w.status).join(', ')} → Estado: ${step.status}`);
      }

      console.log(`✅ Step ${config.step} (${config.title}): ${assignedWeeks.length} semanas asignadas, estado: ${step.status}`);
    });

    console.log('📊 progressSteps actualizado:', this.progressSteps);
  }

  /**
   * Genera el texto del rango de semanas para mostrar en el statusText
   */
  getWeekRange(start: number, end: number): string {
    if (start === end) {
      return `${start}`;
    } else if (end - start === 1) {
      return `${start} y ${end}`;
    } else {
      return `${start} a ${end}`;
    }
  }
}
